/**
 * Swarm publishing: the studio is the only origin of an ompeer.com stream. The program stream
 * is segmented in the browser (WebCodecs → fMP4), each segment is encrypted with the stream's
 * AES-128-GCM key and hashed, and the studio seeds the P2P swarm over DataChannels. The control
 * plane (api.ompeer.com) only hands out the key and the seed's signalling credentials.
 *
 * When the channel had ads enabled at go-live (`mediaProxy.enabled` in the answer), every segment published to
 * the swarm also goes, byte for byte, to OMP's media proxy (`PUT /api/streams/:id/media/:segmentId`), from
 * which the ad server pulls it. That copy never holds up the swarm: it is sent after the publish, one at a time.
 *
 * When go-live also says `mediaProxy.hidesSeed` (an affiliate's channel), the proxy is the viewers' only origin: the
 * tracker hides this seed from them and the seed answers no offer (no PeerConnection, so this computer's address
 * never reaches a viewer). If the proxy stops taking segments, the viewers would get nothing new: the studio then
 * falls back, by itself, to answering them directly (the streamer's decision, recorded in the media proxy spec), says
 * so in plain words (`exposed`, `privacyWarning`: their address is visible), tells the tracker (`seed-exposure`, which
 * the backend logs) and goes back behind the proxy once it takes HIDDEN_PROXY_RECOVERY segments in a row again.
 */
import { CLOSE_REPLACED, CLOSE_STREAM_ENDED, P2PEngine, startIceLease, type P2PEngineConfig, type PublishSegmentInput } from '@ohmyprime/p2p-engine';
import { COMPAT_RENDITION_ID, aesGcmEncrypt, base64ToBytes, importAesKey, parseMediaTransportProfile, segmentAad, segmentIdForSeq, sha256Hex, type MediaTransportProfile } from '@ohmyprime/p2p-shared';
import { parseInit, programFrameSourceId, compatVideo, createSegmenter, maxVideoQueueFor, preflightPublication, type CompatVideo, type PublicationPreflight, type EncodedPacketSink, type InitInfo, type MediaSegment, type Segmenter, type SegmenterOptions } from '@ohmyprime/segmenter';
import type { IceServerConfig } from '../core/model';
import type { NetDeps, SwarmCompatStats, SwarmInfoPatch, SwarmPublisher, SwarmStartOptions, SwarmState, SwarmStats } from './api';
import { backoffDelay } from './backoff';
import { clipFulfillerOf, type ClipFulfiller } from './clip-requests';
import { createStore, errorMessage } from './store';
import { isTokenSafeEndpoint, isWhipEndpoint } from './whip-http';
import { ProxyUploadMonitor } from './proxy-upload-outcome';
import { WitnessPublication, type WitnessPublisherIdentity } from './witness-publication';
import { RuntimePublication } from './runtime-publication';

const SCOPE = 'net.swarm';

export const HEARTBEAT_INTERVAL_MS = 30_000;
/** The first thumbnail goes up once the encoder has frames; later ones about once a minute. */
export const THUMBNAIL_FIRST_DELAY_MS = 5000;
export const THUMBNAIL_INTERVAL_MS = 60_000;
/** A 429 moves the next upload to its retryAfterMs, never sooner than this nor later than the regular tick. */
const THUMBNAIL_MIN_RETRY_MS = 1000;
/** How long a frame grab waits for the track: a program track with nothing on it never delivers one. */
export const THUMBNAIL_CAPTURE_TIMEOUT_MS = 2000;
const THUMBNAIL_MAX_WIDTH = 640;
const THUMBNAIL_MAX_HEIGHT = 360;
const THUMBNAIL_JPEG_QUALITY = 0.7;
const STATS_INTERVAL_MS = 1000;
const JOIN_TIMEOUT_MS = 15_000;
const END_TIMEOUT_MS = 5000;
/** How long stop() lets an in-flight go-live finish so that the session it opens can be ended. */
const STOP_START_GRACE_MS = 8000;
const SEED_FIRST_HOP_FANOUT = 3;
/** The H.264 fallback seed serves one first-hop viewer; its media proxy is the other source (see the fallback plan). */
export const COMPAT_SEED_FANOUT = 1;
/** Compat registrations at most this often (the backend allows 20 a minute per stream): init changes are debounced. */
export const COMPAT_REGISTER_SPACING_MS = 3000;
/** Attempts of one compat registration (429 waits its Retry-After; 5xx and network errors back off). */
export const COMPAT_REGISTER_ATTEMPTS = 4;
/** After a non-final tracker close of the compat seed, the lane waits this long (main's re-go-live goes first). */
export const COMPAT_REJOIN_DELAY_MS = 1000;
/** Compat parts waiting to be published beyond this are dropped. */
const COMPAT_MAX_PENDING = 15;
/**
 * Load watch: main dropping this many frames a second for 2 s, or its encoder queue for 3 s at the depth where the
 * segmenter starts dropping frames (maxVideoQueueFor(fps)), sheds compat.
 */
export const SHED_DROPPED_FRAMES_PER_S = 3;
/** After stepping the fallback down to 480p, upload backlog is ignored this long before it sheds the lane. */
export const COMPAT_STEP_DOWN_QUIET_MS = 10_000;
/** Segments waiting to be published beyond this are dropped, oldest first, so the swarm stays at the live edge. */
export const MAX_PENDING_SEGMENTS = 3;
/** Segments waiting to go to the media proxy beyond this are not sent: the proxy stays at the live edge too. */
export const MAX_PENDING_PROXY_SENDS = 3;
/** Sends of the init to the media proxy, and the backoff between them (0.5 s, 1 s, 2 s). */
export const PROXY_INIT_ATTEMPTS = 4;
export const PROXY_INIT_RETRY_MS = 500;
/** Media segments in a row that miss the proxy before a hidden broadcast warns that its viewers get nothing new. */
export const HIDDEN_PROXY_MISSES = 3;
/** Segments in a row the proxy must take again before an exposed studio goes back behind it. */
export const HIDDEN_PROXY_RECOVERY = 5;
/** How long a stop waits for the last segment to reach the media proxy. */
const PROXY_FLUSH_TIMEOUT_MS = 2000;
/** Request and response of one pipelined part beyond the window it waits in: two 200 ms round trips. */
export const PROXY_PIPELINE_SLACK_MS = 400;

/** The slowest uplink a keyframe part is given time for: below it a 5 Mbit/s broadcast cannot keep up anyway. */
export const PROXY_KEYFRAME_MIN_BPS = 3_000_000;

/**
 * How long a pipelined part upload (`capabilities.mediaIngest`) may take, from the moment it goes on the wire.
 *
 * A dependent part: with `maxInFlight` uploads on the wire and a part every `partMs`, a lane keeps up as long as each
 * upload takes less than maxInFlight × partMs (Little's law). One still running after that window, plus the slack of
 * its own request and response, holds a slot the next parts need, and is given up:
 *
 *   dependent = maxInFlight × partMs + PROXY_PIPELINE_SLACK_MS            (4 × 200 + 400 = 1200 ms)
 *
 * An independent (keyframe) part is several times larger than the others. A fixed 1200 ms would lose every GOP at
 * once when the uplink makes its keyframe take longer than that, so it gets what its bytes need at
 * PROXY_KEYFRAME_MIN_BPS, never less than a dependent part:
 *
 *   keyframe = max(dependent, PROXY_PIPELINE_SLACK_MS + bytes × 8 / PROXY_KEYFRAME_MIN_BPS)
 *              (300 KB → 1200 ms, 600 KB → 2000 ms)
 *
 * Not one budget for the whole GOP: a slow keyframe would spend the time of its dependents, and the GOP would then
 * fail all together instead of only when its keyframe is too slow. A dependent's own deadline is moved past its
 * predecessor's instead (see sendToProxy): the server stores it only after that one, so it cannot end before it.
 */
export function pipelinedPartDeadlineMs(maxInFlight: number, partMs: number, keyframeBytes?: number): number {
  const dependent = maxInFlight * partMs + PROXY_PIPELINE_SLACK_MS;
  if (keyframeBytes === undefined) return dependent;
  return Math.max(dependent, PROXY_PIPELINE_SLACK_MS + Math.ceil((keyframeBytes * 8 / PROXY_KEYFRAME_MIN_BPS) * 1000));
}
/** Re-go-live backoff: 1 s, 2 s, 4 s … capped at 30 s, for at most this many attempts. */
export const RELIVE_MAX_ATTEMPTS = 10;
const RELIVE_BASE_MS = 1000;
const RELIVE_MAX_DELAY_MS = 30_000;
const TAKEN_OVER_MESSAGE =
  'Another studio went live with this stream key and took the stream over (4409). This studio stopped streaming.';
const STREAM_ENDED_MESSAGE = 'The stream was ended from the server (4410). This studio stopped streaming.';

/**
 * Tracker closes that end this studio's broadcast for good: 4409 (another seed took over) and 4410
 * (the operator or the backend ended the stream on purpose). Going live again would fight them.
 * Every other rejection (4401 after the engine's own retry, 4408 join timeout) means re-go-live.
 */
function terminalRejection(code: number): string | null {
  if (code === CLOSE_REPLACED) return TAKEN_OVER_MESSAGE;
  if (code === CLOSE_STREAM_ENDED) return STREAM_ENDED_MESSAGE;
  return null;
}

export function reliveDelayMs(attempt: number): number {
  return Math.min(RELIVE_MAX_DELAY_MS, RELIVE_BASE_MS * 2 ** Math.min(attempt, 16));
}

/**
 * go-live requires a codecs string, but the segmenter only knows the exact one (profile and level
 * from the encoder's avcC) when it emits the init segment, at the end of the first segment. The
 * control plane has no endpoint to update it, so v1 sends this best-effort value (High profile,
 * AAC-LC), and `POST /api/session` may echo it back to viewers. It is not authoritative: viewers
 * must create their SourceBuffer from the codecs in the tracker's live-window (and the init
 * `segment-announce`), which carry the exact string this publisher passes to publishInit.
 */
export const GO_LIVE_CODECS_GUESS = 'avc1.640028,mp4a.40.2';

/** The parts of P2PEngine the publisher uses; narrowed so tests can substitute a fake. */
export interface SwarmEngine {
  configureIceServers?(servers: RTCIceServer[], policy?: RTCIceTransportPolicy): void;
  join(streamId: string, renditionId?: string): Promise<void>;
  publishInit(bytes: ArrayBuffer | Uint8Array, opts?: { codecs?: string }): Promise<unknown>;
  publishSegment(input: PublishSegmentInput): Promise<unknown>;
  endStream(reason?: string): void;
  destroy(): void;
  getMetrics(): { bytesUploaded: number; segmentsPublished: number };
  getPeerCount(): number;
  /** Viewers known to the tracker (from its last peer-list), excluding seeds. */
  getSwarmSize(): number;
  /**
   * Engine events the publisher listens to. `signaling-rejected` fires when the tracker closes the
   * seed socket with 4401/4408/4409/4410 (`willRetry` says whether the engine keeps retrying). 4409
   * and 4410 stop this publisher; 4401 and 4408 make it go live again. Optional so that an
   * engine without the event still works (the heartbeat then is the only liveness signal).
   */
  on?(event: 'signaling-rejected', handler: (info: SignalingRejection) => void): unknown;
  /** The engine joined again after its socket dropped (a tracker that restarted has forgotten what the seed said). */
  on?(event: 'signaling-reconnected', handler: () => void): unknown;
  /**
   * The privacy fallback of a hidden broadcast: open (answer viewers, `fanout` first-hop) or close (answer nobody,
   * drop every viewer connection) this seed to viewers, and tell the tracker which. Optional: an engine without them
   * cannot fall back, and the studio then only warns.
   */
  setViewerAccess?(open: boolean, fanout: number): void;
  announceExposure?(exposed: boolean, reason?: string): void;
}

export interface SignalingRejection {
  code: number;
  reason: string;
  /** True when the engine reconnects by itself; the publisher then leaves it alone. */
  willRetry?: boolean;
}

/** The seed token travels in the signalling URL: only wss:, or ws: to a loopback host. */
export function isSafeSignalingUrl(value: string): boolean {
  try {
    const url = new URL(value);
    if (url.protocol !== 'ws:' && url.protocol !== 'wss:') return false;
    return isTokenSafeEndpoint(value.replace(/^ws/i, 'http'));
  } catch {
    return false;
  }
}

export interface SwarmPublisherDeps extends NetDeps {
  preflightPublication?: typeof preflightPublication;
  fetch?: typeof fetch;
  createEngine?: (config: P2PEngineConfig) => SwarmEngine;
  createSegmenter?: (stream: MediaStream, sink: EncodedPacketSink, options: SegmenterOptions) => Segmenter;
  /** The current program frame as a small image, or null when there is none (audio only, no frame yet). */
  captureThumbnail?: (stream: MediaStream) => Promise<Blob | null>;
  /**
   * Whether the server at an API URL took a go-live fallback last time (`capabilities.fallbackRenditions`); undefined
   * when not known yet. Auto skips the fallback encoder probe for a server known not to take it.
   */
  fallbackCapability?: { get(apiUrl: string): boolean | undefined; set(apiUrl: string, supported: boolean): void };
}

const FALLBACK_CAPABILITY_KEY = 'omp.studio.fallbackRenditions:';

/** Per API URL, in this browser's localStorage; nothing remembered where storage is unavailable. */
export const storedFallbackCapability: NonNullable<SwarmPublisherDeps['fallbackCapability']> = {
  get(apiUrl) {
    try {
      const v = globalThis.localStorage?.getItem(FALLBACK_CAPABILITY_KEY + apiUrl);
      return v === '1' ? true : v === '0' ? false : undefined;
    } catch {
      return undefined;
    }
  },
  set(apiUrl, supported) {
    try { globalThis.localStorage?.setItem(FALLBACK_CAPABILITY_KEY + apiUrl, supported ? '1' : '0'); } catch { /* not remembered */ }
  },
};

/**
 * Reads one frame off the program video track, the way the segmenter does, scales it down to fit
 * 640×360 and encodes it as JPEG: a few tens of KiB, far below the control plane's 512 KiB cap.
 */
export async function captureThumbnail(stream: MediaStream): Promise<Blob | null> {
  const track = stream.getVideoTracks()[0];
  if (!track || track.readyState !== 'live' || typeof MediaStreamTrackProcessor !== 'function') return null;
  // Its own reader on the same track: the segmenter's reader keeps receiving every frame.
  const reader = new MediaStreamTrackProcessor({ track: track as MediaStreamVideoTrack, maxBufferSize: 1 }).readable.getReader();
  // Cancelling settles the pending read as done.
  const timer = setTimeout(() => void reader.cancel().catch(() => undefined), THUMBNAIL_CAPTURE_TIMEOUT_MS);
  try {
    const { value: frame } = await reader.read();
    if (!frame) return null;
    try {
      const scale = Math.min(1, THUMBNAIL_MAX_WIDTH / frame.displayWidth, THUMBNAIL_MAX_HEIGHT / frame.displayHeight);
      const canvas = new OffscreenCanvas(
        Math.max(1, Math.round(frame.displayWidth * scale)),
        Math.max(1, Math.round(frame.displayHeight * scale)),
      );
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(frame, 0, 0, canvas.width, canvas.height);
      return await canvas.convertToBlob({ type: 'image/jpeg', quality: THUMBNAIL_JPEG_QUALITY });
    } finally {
      frame.close();
    }
  } finally {
    clearTimeout(timer);
    void reader.cancel().catch(() => undefined);
  }
}

interface GoLive {
  broadcastId: string | null;
  transportProfile?: MediaTransportProfile;
  streamId: string;
  keyId: string;
  aesKey: string;
  seedToken: string;
  ts: number;
  signalingUrl: string;
  seedPeerId: string;
  /** The session's segments also go to OMP's media proxy. */
  mediaProxy: boolean;
  /** Viewers get the stream from the media proxy only; this seed is hidden from them. */
  hidesSeed: boolean;
  proxyActive: boolean;
  proxyPollAfterMs: number;
  /** The server took the H.264 fallback: the compat seed's credentials (absent: AV1 only). */
  fallback: CompatGoLive | null;
  /** `capabilities.fallbackRenditions`: the server takes a go-live fallback (STUDIO_FALLBACK_RENDITIONS=accept). */
  fallbackRenditions: boolean;
  /**
   * `capabilities.mediaIngest`: the media proxy takes uploads at one URL (`PUT media`, X-Omp-Segment-Id), and up to
   * `maxInFlight` per lane at once, stored in order. Null: one at a time, at `media/<segmentId>`, as before.
   */
  mediaIngest: { maxInFlight: number } | null;
}

/** go-live's `fallback` answer. */
interface CompatGoLive {
  seedToken: string;
  seedPeerId: string;
  ts: number;
  transportProfile: MediaTransportProfile;
  mediaProxy: { enabled: boolean; active: boolean; pollAfterMs: number };
}

class SwarmError extends Error {
  constructor(
    message: string,
    readonly retryable: boolean,
  ) {
    super(message);
  }
}

/** Thrown inside a start that was overtaken by stop(); never surfaced. */
class Superseded extends Error {}

/** The H.264 fallback cannot run in this broadcast; `withdraw` says whether the server still holds a declaration to DELETE. */
class CompatUnavailable extends Error {
  constructor(message: string, readonly withdraw: boolean) {
    super(message);
  }
}

interface PublishTask {
  kind: 'segment' | 'other';
  segment?: MediaSegment;
  label: string;
  run(): Promise<void>;
}

/**
 * The media-proxy state of one rendition. Main's lives on the Run itself (its fields predate the fallback lane);
 * compat's on its CompatLane. Each lane has its own ordered upload chain and budget.
 */
interface ProxyLane {
  mediaProxy: boolean;
  proxyActive: boolean;
  proxyAbort: AbortController;
  proxyChain: Promise<void>;
  proxyPending: number;
  proxyPendingBytes: number;
  proxyInitRevision: number;
  proxyInitReady: boolean;
  proxyInitResent: boolean;
  proxyPart: { seq: number; gopStartSeq?: number } | null;
  /** Pipelined uploads (`mediaIngest`) on the wire; the chain only puts them there, in order. */
  proxyInFlight: Set<Promise<void>>;
  /**
   * Pipelined: the last part put on the wire, when it must have been answered (performance.now() ms), and its GOP. Once a
   * part of the GOP did not get through (`failedSeq`), the parts after it are not sent, and those on the wire
   * (`onTheWire`) are aborted, until the next independent part; the ones before it carry on.
   */
  proxySent: { seq: number; gopStartSeq?: number; deadlineAt: number; gop: ProxyGop } | null;
  init: { bytes: Uint8Array; codecs: string } | null;
}

/** A GOP being pipelined to the media proxy (see ProxyLane.proxySent). */
interface ProxyGop {
  failedSeq: number | null;
  onTheWire: Map<number, AbortController>;
}

/** The H.264 `compat` rendition next to an AV1 main: its own seed engine, registration and proxy lane. */
interface CompatLane extends ProxyLane {
  state: SwarmCompatStats['state'];
  reason: string | null;
  video: CompatVideo;
  /** Declared and checked codecs (from the preflight); an init with others ends the lane. */
  codecs: string;
  transportProfile: MediaTransportProfile;
  /** keyId of the go-live this lane's credentials belong to. */
  generation: string;
  streamId: string;
  signalingUrl: string;
  seed: { seedToken: string; seedPeerId: string; ts: number };
  engine: SwarmEngine | null;
  init: { bytes: Uint8Array; codecs: string; sha256: string | null } | null;
  /** The init published on `engine`; parts of another init are never published to it. */
  publishedInit: CompatLane['init'];
  registeredSha: string | null;
  lastRegisterAt: number;
  syncing: Promise<void> | null;
  resync: boolean;
  publishedPart: MediaSegment | null;
  chain: Promise<void>;
  pending: number;
  droppedSegments: number;
  encodedSamples: { bytes: number; durationMs: number }[];
  lastEncodedAt: number | null;
  pollAfterMs: number;
  /** The withdrawal of a shed lane (stream-ended, DELETE, destroy); teardown waits for it. */
  retiring: Promise<void> | null;
  /** Non-final tracker closes of the compat seed in this session, each answered by registering and joining again. */
  rejoins: number;
}

interface Run {
  publicationGeneration: number;
  broadcastId: string | null;
  preflight: PublicationPreflight | null;
  /** The H.264 fallback this broadcast asks go-live for: a proven encoder, its size and transport profile. */
  compatPlan: { video: CompatVideo; videoConfig: VideoEncoderConfig; codecs: string; transportProfile: MediaTransportProfile } | null;
  compat: CompatLane | null;
  /** Why the asked-for fallback is not running when there is no lane to say it (preflight or go-live refused it). */
  compatNote: string | null;
  /** Latest ICE servers of main's lease: the compat seed shares it. */
  ice: { stunUrls: string[]; turnServers: RTCIceServer[] } | null;
  /** Main proxy sends refused by the upload budget (the load watch reads it). */
  proxyBudgetDrops: number;
  /**
   * Main parts dropped because the publish queue exceeded its media budget: the upload falling behind. Unlike
   * droppedSegments it leaves out parts dropped between sessions (a re-go-live), which say nothing about the upload.
   */
  publishBudgetDrops: number;
  load: { primed: boolean; droppedFrames: number; publishDrops: number; proxyDrops: number; dropTicks: number; queueTicks: number; quietUntil: number };
  options: SwarmStartOptions;
  transportProfile?: MediaTransportProfile;
  publishedPart: MediaSegment | null;
  proxyPart: { seq: number; gopStartSeq?: number } | null;
  proxyInFlight: ProxyLane['proxyInFlight'];
  proxySent: ProxyLane['proxySent'];
  /** The current go-live's `capabilities.mediaIngest` (both lanes use it). */
  mediaIngest: GoLive['mediaIngest'];
  proxyPendingBytes: number;
  proxyInitRevision: number;
  proxyInitReady: boolean;
  base: string;
  streamId: string;
  /** Aborts this run's HTTP requests. Fired last, so an in-flight go-live can still be ended. */
  abort: AbortController;
  /** Fired as soon as the run is stopped or failed: interrupts a join in progress and the relive backoff. */
  cancel: AbortController;
  /** keyId of the latest successful go-live, sent with /end so the backend can ignore a stale end. */
  liveKeyId: string | null;
  /** The running re-go-live loop, if any; retiring the run waits for it. */
  reliveDone: Promise<void> | null;
  establishing: boolean;
  inFlight: Promise<void> | null;
  /** go-live succeeded: the control plane must be told when the session ends. */
  wentLive: boolean;
  engine: SwarmEngine | null;
  segmenter: Segmenter | null;
  key: CryptoKey | null;
  keyId: string;
  /** Publishing runs one task at a time so the swarm sees init and segments in capture order. */
  queue: PublishTask[];
  pumping: Promise<void> | null;
  /** The last init segment, re-published to every new engine after a re-go-live. */
  init: { bytes: Uint8Array; codecs: string } | null;
  /** This session sends what it publishes to the media proxy (go-live said so, and the proxy has not closed). */
  mediaProxy: boolean;
  proxyActive: boolean;
  proxyPollAfterMs: number;
  proxyAbort: AbortController;
  /** Sends to the media proxy, one at a time in publish order, and how many are waiting. */
  proxyChain: Promise<void>;
  proxyPending: number;
  /** The init was sent again after an INIT_REQUIRED and has not got through since. */
  proxyInitResent: boolean;
  /** The current go-live hides this seed from viewers (see GoLive.hidesSeed). */
  hidesSeed: boolean;
  /** Media segments in a row that did not reach the proxy (refused, failed, or dropped behind the queue). */
  proxyFailures: number;
  /** Hidden broadcast whose studio now answers viewers directly: its address is reachable from them. */
  exposed: boolean;
  /** The proxy is closed for this broadcast (off or ended): no way back behind it until the next go-live. */
  exposurePermanent: boolean;
  /** Segments in a row the proxy took since the exposure. */
  proxyStreak: number;
  droppedSegments: number;
  /** A re-go-live is in progress; no engine is installed meanwhile. */
  reliving: boolean;
  /** Joined by a re-go-live but not yet installed through the publish queue. */
  pendingEngine: SwarmEngine | null;
  reliveTimer: ReturnType<typeof setTimeout> | null;
  heartbeatTimer: ReturnType<typeof setTimeout> | null;
  thumbnailTimer: ReturnType<typeof setTimeout> | null;
  /** A thumbnail is being captured or uploaded; ticks that fall meanwhile are skipped. */
  thumbnailBusy: boolean;
  statsTimer: ReturnType<typeof setInterval> | null;
  heartbeatFailures: number;
  liveSeq: number | null;
  lastCapturedAt: number | null;
  codecs: string | null;
  lastBytes: number;
  lastBytesAt: number;
  uploadKbps: number;
  encodedSamples: { bytes: number; durationMs: number }[];
  lastEncodedAt: number | null;
  closed: boolean;
}

const IDLE: SwarmState = {
  status: 'idle',
  streamId: null,
  seedPeerId: null,
  error: null,
  reconnectAttempts: 0,
  viewers: 0,
  stats: null,
  hidesSeed: false,
  privacyWarning: null,
  exposed: false,
};

/** `sk_<streamId>.<channelId>.<hex>` → streamId, or null when the key is not in that shape. */
export function parseStreamKey(key: string): { streamId: string; channelId: string } | null {
  const match = /^sk_([A-Za-z0-9_-]+)\.([A-Za-z0-9_-]+)\.([0-9a-f]+)$/.exec(key.trim());
  return match ? { streamId: match[1]!, channelId: match[2]! } : null;
}

export interface SwarmFailure {
  message: string;
  retryable: boolean;
}

/** Maps a control-plane answer to an operator-facing message, like describeWhipFailure does for WHIP. */
export function describeSwarmFailure(status: number, code?: string): SwarmFailure {
  if (status === 401 || code === 'BAD_STREAM_KEY') {
    return { message: 'ompeer.com rejected the stream key (401). Copy it again from your channel page.', retryable: false };
  }
  if (status === 403 || code === 'STREAM_KEY_MISMATCH') {
    return { message: 'The stream key belongs to a different stream (403).', retryable: false };
  }
  if (status === 404) return { message: 'ompeer.com does not know this stream (404).', retryable: false };
  if (status === 400) return { message: 'ompeer.com rejected the go-live request as malformed (400).', retryable: false };
  if (status === 429) return { message: 'ompeer.com is rate limiting this studio (429).', retryable: true };
  if (status >= 500) return { message: `ompeer.com failed (${status}).`, retryable: true };
  return { message: `ompeer.com refused to start the stream (${status}).`, retryable: status === 408 };
}

/** fetch() rejects with a bare TypeError for DNS, TLS, connection and CORS failures alike. */
export const SWARM_NETWORK_FAILURE: SwarmFailure = {
  message:
    'Could not reach the ompeer.com API. Check the API URL and your connection; the server must also allow ' +
    'cross-origin requests from this page (CORS).',
  retryable: true,
};

function splitIceServers(servers: IceServerConfig[] | undefined): { stunUrls: string[]; turnServers: RTCIceServer[] } {
  const stunUrls: string[] = [];
  const turnServers: RTCIceServer[] = [];
  for (const server of servers ?? []) {
    const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
    const stun = urls.filter((u) => /^stuns?:/i.test(u));
    const turn = urls.filter((u) => /^turns?:/i.test(u));
    stunUrls.push(...stun);
    if (turn.length > 0) turnServers.push({ urls: turn, username: server.username, credential: server.credential });
  }
  return { stunUrls, turnServers };
}

function parseGoLive(body: unknown): GoLive | null {
  if (typeof body !== 'object' || body === null) return null;
  const b = body as Record<string, unknown>;
  const strings = ['streamId', 'keyId', 'aesKey', 'seedToken', 'signalingUrl', 'seedPeerId'] as const;
  if (!strings.every((k) => typeof b[k] === 'string' && b[k] !== '') || typeof b.ts !== 'number') return null;
  const proxy = b.mediaProxy;
  const mediaProxy = typeof proxy === 'object' && proxy !== null && (proxy as Record<string, unknown>).enabled === true;
  const policy = proxy as Record<string, unknown> | null;
  // An affiliate's broadcast that feeds the proxy hides its seed from viewers.
  const hidesSeed = mediaProxy && policy?.hidesSeed === true;
  const proxyActive = mediaProxy && policy?.active !== false;
  const proxyPollAfterMs = mediaProxy && typeof policy?.pollAfterMs === 'number' && Number.isFinite(policy.pollAfterMs)
    ? Math.max(1000, Math.min(HEARTBEAT_INTERVAL_MS, policy.pollAfterMs)) : HEARTBEAT_INTERVAL_MS;
  const capabilities = (typeof b.capabilities === 'object' && b.capabilities !== null ? b.capabilities : {}) as Record<string, unknown>;
  const ingest = (typeof capabilities.mediaIngest === 'object' && capabilities.mediaIngest !== null ? capabilities.mediaIngest : {}) as Record<string, unknown>;
  const maxInFlight = ingest.maxInFlight;
  const mediaIngest = ingest.path === 'header' && typeof maxInFlight === 'number' && Number.isInteger(maxInFlight) && maxInFlight >= 1
    ? { maxInFlight: Math.min(8, maxInFlight) } : null;
  return { ...(b as unknown as GoLive), broadcastId: typeof b.broadcastId === 'string' && b.broadcastId.length > 0 && b.broadcastId.length <= 128 ? b.broadcastId : null, mediaProxy, hidesSeed, proxyActive, proxyPollAfterMs, fallback: parseCompatGoLive(b.fallback),
    fallbackRenditions: capabilities.fallbackRenditions === true, mediaIngest };
}

/** go-live's `fallback`, or null when absent (a server without STUDIO_FALLBACK_RENDITIONS=accept) or malformed. */
function parseCompatGoLive(value: unknown): CompatGoLive | null {
  if (typeof value !== 'object' || value === null) return null;
  const f = value as Record<string, unknown>;
  if (f.renditionId !== COMPAT_RENDITION_ID || f.protocolVersion !== 2 || typeof f.ts !== 'number') return null;
  if (typeof f.seedToken !== 'string' || !f.seedToken || typeof f.seedPeerId !== 'string' || !f.seedPeerId) return null;
  const transportProfile = parseMediaTransportProfile(f.transportProfile);
  if (!transportProfile) return null;
  const p = (typeof f.mediaProxy === 'object' && f.mediaProxy !== null ? f.mediaProxy : {}) as Record<string, unknown>;
  const pollAfterMs = typeof p.pollAfterMs === 'number' && Number.isFinite(p.pollAfterMs)
    ? Math.max(1000, Math.min(HEARTBEAT_INTERVAL_MS, p.pollAfterMs)) : HEARTBEAT_INTERVAL_MS;
  return { seedToken: f.seedToken, seedPeerId: f.seedPeerId, ts: f.ts, transportProfile,
    mediaProxy: { enabled: p.enabled === true, active: p.enabled === true && p.active !== false, pollAfterMs } };
}

/** AES-GCM context of a protocolVersion 2 rendition part, as the engine decrypts it (`${keyId}:${rendition}:${segmentId}`). */
export function compatAad(keyId: string, segmentId: string): string {
  return `${keyId}:${COMPAT_RENDITION_ID}:${segmentId}`;
}

/** The tracker reads a seed's rendition from its signalling URL. */
function withRendition(url: string, renditionId: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}renditionId=${encodeURIComponent(renditionId)}`;
}

/** go-live `fallback` the backend accepts (its renditionVideo and compat limits), or why this one would be refused. */
export function fallbackRequest(r: { options: SwarmStartOptions }, plan: { codecs: string; transportProfile: MediaTransportProfile },
  video: CompatVideo): Record<string, unknown> | string {
  const o = r.options;
  const main = { width: o.width, height: o.height, fps: o.fps, videoBitrate: Math.round(o.videoKbps * 1000), audioBitrate: Math.round(o.audioKbps * 1000) };
  const fallback = { width: video.width, height: video.height, fps: video.fps, videoBitrate: Math.round(video.videoKbps * 1000), audioBitrate: main.audioBitrate };
  const int = (v: number, min: number, max: number) => Number.isSafeInteger(v) && v >= min && v <= max;
  for (const [name, v] of [['main', main], ['fallback', fallback]] as const) {
    if (!int(v.width, 2, 7680) || !int(v.height, 2, 4320) || v.width % 2 || v.height % 2) return `${name} size ${v.width}x${v.height} must be even, 2-7680 x 2-4320`;
    if (!int(v.fps, 1, 120)) return `${name} fps ${v.fps} must be a whole number from 1 to 120`;
    if (!int(v.videoBitrate, 32_000, 50_000_000)) return `${name} video bitrate ${v.videoBitrate} must be 32 kb/s to 50 Mb/s`;
    if (!int(v.audioBitrate, 32_000, 512_000)) return `audio bitrate ${v.audioBitrate} must be 32 to 512 kb/s`;
  }
  if (!/^avc[13]\./i.test(plan.codecs) || plan.codecs.length > 200) return `fallback codecs ${plan.codecs} are not H.264`;
  if (Math.max(fallback.width, fallback.height) > 1280 || Math.min(fallback.width, fallback.height) > 720 ||
      fallback.width > main.width || fallback.height > main.height) return 'the fallback must be at most 720p and no larger than main';
  if (fallback.fps > Math.min(main.fps, 30)) return 'the fallback fps must be at most 30 and no higher than main';
  if (fallback.videoBitrate > 4_000_000) return 'the fallback video bitrate must be at most 4 Mb/s';
  return { codecs: plan.codecs, ...fallback, transportProfile: plan.transportProfile, main };
}

const compatGone = (c: CompatLane): boolean => c.state === 'shed' || c.state === 'unavailable';

const sameProfile = (a: MediaTransportProfile, b: MediaTransportProfile): boolean =>
  Object.entries(a).every(([key, value]) => b[key as keyof MediaTransportProfile] === value);

export function createSwarmPublisher(deps: SwarmPublisherDeps): SwarmPublisher {
  const { logger } = deps;
  const doFetch: typeof fetch = deps.fetch ?? ((input, init) => fetch(input, init));
  const makeEngine = deps.createEngine ?? ((config: P2PEngineConfig): SwarmEngine => new P2PEngine(config));
  const makeSegmenter = deps.createSegmenter ?? createSegmenter;
  const preflight = deps.preflightPublication ?? preflightPublication;
  const grabThumbnail = deps.captureThumbnail ?? captureThumbnail;
  const fallbackCapability = deps.fallbackCapability ?? storedFallbackCapability;
  const store = createStore<SwarmState>(IDLE);
  let run: Run | null = null;
  const uploads = new ProxyUploadMonitor();
  let publicationGeneration = 0;
  let witness: WitnessPublication | null = null;
  let runtimePublication:RuntimePublication|null=null;
  // Bounded publisher-lifetime ledger survives stop/restart and failed attempts. Overflow fails closed.
  const witnessedEndings = new Set<string>();
  function getWitnessIdentity(): WitnessPublisherIdentity | null {
    const r = run;
    const track = r?.options.stream.getVideoTracks?.()[0];
    const sourceId = track && programFrameSourceId(track);
    if (!r || r.closed || r.cancel.signal.aborted || r.reliving || !r.engine || !r.key || !r.broadcastId ||
        !r.init || !r.segmenter?.beginFrameEvidence || !track || track.readyState === 'ended' || !sourceId) return null;
    return Object.freeze({ generation: r.publicationGeneration, streamId: r.streamId,
      broadcastId: r.broadcastId, keyId: r.keyId, track, sourceId });
  }
  function sameWitnessIdentity(identity: WitnessPublisherIdentity): boolean {
    const current = getWitnessIdentity();
    return !!current && Object.entries(current).every(([key, value]) => identity[key as keyof WitnessPublisherIdentity] === value);
  }
  const invalidatePublication = (r: Run): void => { r.publicationGeneration = ++publicationGeneration; runtimePublication?.cancel(); witness?.cancel(); uploads.cancel(); };
  const iceLeases = new Map<SwarmEngine, { stop(): void }>();
  function destroyEngine(engine: SwarmEngine | null): void {
    if (!engine) return;
    iceLeases.get(engine)?.stop();
    iceLeases.delete(engine);
    engine.destroy();
  }
  /**
   * Teardown of runs that were stopped or failed. start() waits for it, so a late `/end` of an old
   * run can never land after (and end) the next go-live.
   */
  let retiring: Promise<void> = Promise.resolve();

  function retire(work: () => Promise<void>): Promise<void> {
    const done = work().catch((err: unknown) => {
      logger.log('warn', SCOPE, `cleanup failed: ${errorMessage(err)}`);
    });
    retiring = Promise.all([retiring, done]).then(() => undefined);
    return done;
  }

  /** Resolves after `ms`, or at once when the run is cancelled. */
  function pause(r: Run, ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      const done = (): void => {
        if (r.reliveTimer) clearTimeout(r.reliveTimer);
        r.reliveTimer = null;
        r.cancel.signal.removeEventListener('abort', done);
        resolve();
      };
      if (r.cancel.signal.aborted) return done();
      r.reliveTimer = setTimeout(done, ms);
      r.cancel.signal.addEventListener('abort', done);
    });
  }

  const apiPath = (r: Run, action: string): string =>
    `${r.base}/api/streams/${encodeURIComponent(r.streamId)}/${action}`;

  const authHeaders = (r: Run, json: boolean): Record<string, string> => ({
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    Authorization: `Bearer ${r.options.streamKey.trim()}`,
  });

  function readStats(r: Run): SwarmStats | null {
    if (!r.engine && !r.reliving) return null;
    let metrics = { bytesUploaded: 0, segmentsPublished: 0 };
    let firstHopPeers = 0;
    let viewers = 0;
    try {
      if (r.engine) {
        metrics = r.engine.getMetrics();
        firstHopPeers = r.engine.getPeerCount();
        viewers = r.engine.getSwarmSize();
      }
    } catch (err) {
      logger.log('debug', SCOPE, `engine metrics failed: ${errorMessage(err)}`);
    }
    const seg = r.segmenter?.getState();
    const encodedDurationMs = r.encodedSamples.reduce((sum, sample) => sum + sample.durationMs, 0);
    const encodedBytes = r.encodedSamples.reduce((sum, sample) => sum + sample.bytes, 0);
    const encodedFresh = r.lastEncodedAt !== null && Date.now() - r.lastEncodedAt <= Math.max(3000, r.options.segmentMs * 3);
    return {
      viewers,
      firstHopPeers,
      uploadKbps: r.uploadKbps,
      encodedKbps: encodedFresh && encodedDurationMs > 0 ? Math.round((encodedBytes * 8) / encodedDurationMs) : null,
      bytesUploaded: metrics.bytesUploaded,
      segmentsPublished: metrics.segmentsPublished,
      liveSeq: r.liveSeq,
      lastCapturedAt: r.lastCapturedAt,
      encoderQueue: seg?.encoderQueue ?? 0,
      droppedFrames: seg?.droppedFrames ?? 0,
      droppedSegments: r.droppedSegments,
      codecs: r.codecs,
      ...(r.compat || r.compatNote ? { compat: compatStats(r, seg?.lanes?.[COMPAT_RENDITION_ID]?.implementation) } : {}),
    };
  }

  function compatStats(r: Run, implementation: string | undefined): SwarmCompatStats {
    const c = r.compat;
    if (!c) return { state: 'unavailable', reason: r.compatNote, width: null, height: null, encoder: null, kbps: null, viewers: 0 };
    const duration = c.encodedSamples.reduce((sum, sample) => sum + sample.durationMs, 0);
    const bytes = c.encodedSamples.reduce((sum, sample) => sum + sample.bytes, 0);
    const fresh = c.lastEncodedAt !== null && Date.now() - c.lastEncodedAt <= 3000;
    let viewers = 0;
    try { viewers = c.engine?.getSwarmSize() ?? 0; } catch { /* metrics only */ }
    return {
      state: c.state, reason: c.reason, width: c.video.width, height: c.video.height,
      encoder: implementation ?? r.compatPlan?.videoConfig.hardwareAcceleration ?? null,
      kbps: !compatGone(c) && fresh && duration > 0 ? Math.round((bytes * 8) / duration) : null,
      viewers,
    };
  }

  function sampleStats(r: Run): void {
    if (run !== r) return;
    const now = Date.now();
    const stats = readStats(r);
    if (!stats) return;
    const elapsed = now - r.lastBytesAt;
    if (elapsed > 0) {
      const delta = Math.max(0, stats.bytesUploaded - r.lastBytes);
      r.uploadKbps = Math.round((delta * 8) / elapsed);
      stats.uploadKbps = r.uploadKbps;
    }
    r.lastBytes = stats.bytesUploaded;
    r.lastBytesAt = now;
    store.set({ stats, viewers: stats.viewers });
    if (r.compat && !compatGone(r.compat)) watchLoad(r, r.compat, now);
  }

  /**
   * The fallback lane is expendable: when main shows strain it goes first. Main dropping frames (CPU) or its encoder
   * queue staying deep sheds it; main's upload falling behind (parts dropped from the publish queue or the proxy
   * queue) first steps it down to 480p, and sheds it if that does not help. A shed lane never comes back in this broadcast.
   */
  function watchLoad(r: Run, c: CompatLane, now: number): void {
    const seg = r.segmenter?.getState();
    if (!seg) return;
    const w = r.load;
    const dropped = seg.droppedFrames - w.droppedFrames;
    const behind = w.primed && !!r.engine && !r.reliving && (r.publishBudgetDrops > w.publishDrops || r.proxyBudgetDrops > w.proxyDrops);
    w.dropTicks = w.primed && dropped >= SHED_DROPPED_FRAMES_PER_S ? w.dropTicks + 1 : 0;
    w.queueTicks = seg.encoderQueue >= maxVideoQueueFor(r.options.fps) ? w.queueTicks + 1 : 0;
    w.droppedFrames = seg.droppedFrames;
    w.publishDrops = r.publishBudgetDrops;
    w.proxyDrops = r.proxyBudgetDrops;
    w.primed = true;
    if (w.dropTicks >= 2) shedCompat(r, c, `main dropped ${dropped} frames a second`);
    else if (w.queueTicks >= 3) shedCompat(r, c, `main's encoder queue stayed at ${seg.encoderQueue} frames`);
    else if (behind && now >= w.quietUntil) {
      if (c.video.height > 480 && c.video.width > 480) {
        c.video = compatVideo({ width: r.options.width, height: r.options.height, fps: r.options.fps, videoKbps: r.options.videoKbps }, 480);
        r.segmenter?.updateLaneEncoding?.(COMPAT_RENDITION_ID, { width: c.video.width, height: c.video.height, videoKbps: c.video.videoKbps });
        w.quietUntil = now + COMPAT_STEP_DOWN_QUIET_MS;
        logger.log('warn', SCOPE, `the upload is falling behind: the H.264 fallback steps down to ${c.video.width}x${c.video.height}`);
      } else {
        shedCompat(r, c, "the upload cannot carry main and the H.264 fallback");
      }
    }
  }

  function pump(r: Run): void {
    if (r.pumping) return;
    r.pumping = (async () => {
      while (r.queue.length > 0) {
        const task = r.queue.shift()!;
        if (r.closed) continue;
        try {
          await task.run();
        } catch (err) {
          logger.log('warn', SCOPE, `${task.label} could not be published: ${errorMessage(err)}`);
        }
      }
      r.pumping = null;
    })();
  }

  function enqueue(r: Run, task: PublishTask): void {
    if (r.closed) return;
    r.queue.push(task);
    if (task.kind === 'segment') {
      // A stalled publish must not build an ever-growing backlog: keep the newest segments.
      const overBudget = (): boolean => {
        const pending = r.queue.filter(t => t.kind === 'segment');
        if (!r.transportProfile) return pending.length > MAX_PENDING_SEGMENTS;
        return pending.length > 15 || pending.reduce((n,t)=>n+(t.segment?.durationMs??0),0)>3000 ||
          pending.reduce((n,t)=>n+(t.segment?.bytes.byteLength??0),0)>16*1024*1024;
      };
      while (overBudget()) {
        const oldest = r.queue.findIndex((t) => t.kind === 'segment');
        const [dropped] = r.queue.splice(oldest, 1);
        r.droppedSegments += 1;
        r.publishBudgetDrops += 1;
        logger.log('warn', SCOPE, `${dropped!.label} dropped: publish queue exceeded its media budget`);
      }
    }
    pump(r);
  }

  /** Resolves once every queued task has run. */
  async function drain(r: Run): Promise<void> {
    while (r.pumping) await r.pumping;
  }

  /** What this run does about clips the control plane asks for. */
  const clipsOf = (r: Run): ClipFulfiller =>
    clipFulfillerOf(r, () => ({
      fetch: doFetch,
      uploadUrl: apiPath(r, 'clips'),
      headers: authHeaders(r, false),
      init: () => r.init?.bytes ?? null,
      signal: r.abort.signal,
      log: (level, message) => logger.log(level, SCOPE, message),
    }));

  /**
   * Privacy fallback of a hidden broadcast: the proxy is not taking the stream, so this studio answers viewers itself,
   * which makes its address reachable from them. Said in the studio in plain words, logged here, and told to the
   * tracker (which tells the viewers' lists and logs it in the backend). Without an engine that can do it, only a
   * warning (the viewers get nothing new).
   */
  function expose(r: Run, why: string, permanent: boolean): void {
    const engine = r.engine;
    if (!r.exposed && engine?.setViewerAccess && engine.announceExposure) {
      engine.setViewerAccess(true, SEED_FIRST_HOP_FANOUT);
      engine.announceExposure(true, why.slice(0, 120));
      r.exposed = true;
    }
    if (r.exposed) r.exposurePermanent = r.exposurePermanent || permanent;
    r.proxyStreak = 0;
    const message = r.exposed
      ? `OMP's media proxy, which keeps your address hidden from viewers, stopped taking the stream (${why}). So that they ` +
        `keep watching, your studio now answers them directly: your IP address is visible to viewers ` +
        (r.exposurePermanent ? 'until this broadcast ends.' : 'until the proxy is back. It goes back behind it by itself.')
      : `Viewers are not receiving new video: OMP's media proxy, which keeps your address hidden from them, stopped ` +
        `taking the stream (${why}). Your address stays hidden.`;
    logger.log('error', SCOPE, message);
    store.set({ exposed: r.exposed, privacyWarning: message });
  }

  /** The proxy takes the stream again: viewers go back to it, and this studio answers nobody once more. */
  function hideAgain(r: Run): void {
    const engine = r.engine;
    if (!r.exposed || !engine?.setViewerAccess || !engine.announceExposure) return;
    engine.announceExposure(false, 'proxy back');
    engine.setViewerAccess(false, 0);
    r.exposed = false;
    r.proxyStreak = 0;
    logger.log('info', SCOPE, "OMP's media proxy takes the stream again: viewers are served by it and your address is hidden again");
    store.set({ exposed: false, privacyWarning: null });
  }

  /**
   * Sends what was just published to the swarm to the media proxy as well, after the sends before it, under the key
   * id the bytes were encrypted with. A segment send that fails is not retried (the proxy only wants the live edge);
   * the init is retried with backoff, since without it the proxy refuses every segment, and sent again when the
   * proxy answers a segment with INIT_REQUIRED. A proxy that says it is off or ended stops this session's sends.
   * `meta` is absent for the init segment.
   */
  function sendToProxy(
    r: Run,
    segmentId: string,
    body: ArrayBuffer | Uint8Array,
    keyId: string,
    meta?: { seq: number; durationMs: number; capturedAt: number; keyframe?: boolean; gopStartSeq?: number },
    /** Main's lane is the run itself; the compat lane has its own chain, budget and `X-Omp-Rendition` header. */
    lane: ProxyLane = r,
    publication?: { sha256: string; generation: number; broadcastId: string | null; engine: SwarmEngine; proxySignal: AbortSignal },
  ): Promise<import('./api').ProxyUploadOutcome> | undefined {
    const main = lane === r;
    const observation = meta && publication ? uploads.begin({
      streamId: r.streamId, broadcastId: publication.broadcastId, keyId,
      renditionId: main ? 'main' : 'compat', segmentId, seq: meta.seq,
      sha256: publication.sha256, generation: publication.generation,
    }, () => run === r && !r.closed && !r.cancel.signal.aborted && r.publicationGeneration === publication.generation &&
      r.broadcastId === publication.broadcastId && r.keyId === keyId &&
      (main ? r.engine === publication.engine : r.compat === lane && r.compat.engine === publication.engine),
      [r.cancel.signal, publication.proxySignal]) : undefined;
    const result = observation?.result;
    if (publication?.proxySignal.aborted) { observation?.finish('cancelled'); return result; }
    // An idle proxy (no viewers for a while) is not a failing one: nothing is sent, nothing is counted as missed.
    if (!lane.mediaProxy || !lane.proxyActive || r.closed) { observation?.finish('skipped'); return result; }
    if (!meta) { lane.proxyInitRevision++; lane.proxyInitReady = false; }
    const initRevision = lane.proxyInitRevision;
    /** The proxy took nothing more of a hidden broadcast: its viewers have no origin left, so the studio shows itself. */
    const lost = (why: string, permanent = false): void => {
      if (!main || !r.hidesSeed || r.keyId !== keyId) return;
      expose(r, why, permanent);
    };
    /** A media segment that did not reach the proxy: on a hidden broadcast, three in a row are lost to viewers. */
    const missed = (why: string): void => {
      if (!main) return;
      r.proxyFailures += 1;
      r.proxyStreak = 0;
      const misses = r.transportProfile ? 15 : HIDDEN_PROXY_MISSES;
      if (r.proxyFailures === misses) lost(`${misses} segments in a row: ${why}`);
    };
    const pendingLimit = r.transportProfile ? 15 : MAX_PENDING_PROXY_SENDS;
    if (meta && (lane.proxyPending >= pendingLimit || (r.transportProfile && lane.proxyPendingBytes + body.byteLength > 16*1024*1024))) {
      if (main) r.proxyBudgetDrops += 1;
      logger.log('warn', SCOPE, `${main ? '' : 'compat '}segment ${meta.seq} not sent to the media proxy: upload queue exceeded its media budget`);
      missed('upload queue exceeded its media budget');
      observation?.finish('skipped'); return result;
    }
    const headers: Record<string, string> = meta
      ? {
          'X-Omp-Seq': String(meta.seq),
          'X-Omp-Duration-Ms': String(Math.round(meta.durationMs)),
          'X-Omp-Captured-At': String(Math.floor(meta.capturedAt)),
          'X-Omp-Key-Id': keyId,
        }
      : { 'X-Omp-Codecs': lane.init?.codecs ?? '' };
    if (!main) headers['X-Omp-Rendition'] = COMPAT_RENDITION_ID;
    // With mediaIngest every upload of the broadcast goes to one URL: the browser preflights it once, not per part.
    const ingest = r.mediaIngest;
    if (ingest) headers['X-Omp-Segment-Id'] = segmentId;
    const pipelined = !!ingest && !!r.transportProfile && !!meta;
    if (pipelined && meta && (meta.keyframe || meta.gopStartSeq !== undefined)) {
      // What the swarm was announced (see onSegment): the server checks it, and orders a dependent part after its predecessor.
      headers['X-Omp-Independent'] = meta.keyframe ? '1' : '0';
      headers['X-Omp-Gop-Start-Seq'] = String(meta.gopStartSeq ?? meta.seq);
    }
    // Only this session's sends: after a re-go-live the bytes belong to a broadcast that is over.
    const proxyAbort = lane.proxyAbort;
    const current = (): boolean => lane.mediaProxy && lane.proxyActive && !proxyAbort.signal.aborted && !r.closed && r.keyId === keyId &&
      (!r.transportProfile || initRevision === lane.proxyInitRevision) && (main || (r.compat === lane && !compatGone(r.compat)));

    /**
     * Whether a media part that failed may be sent once more: the server has not moved past it (nothing at or after
     * its seq was taken on this lane) and, pipelined, no earlier part of its GOP failed. The time bound is in send().
     */
    const edgeAllowsRetry = (): boolean => !!meta && (lane.proxyPart === null || lane.proxyPart.seq < meta.seq);
    /** Parts of the 200 ms profile get the retry; a legacy 1 s segment is sent once, as before. */
    const part = !!meta && !!r.transportProfile;
    /**
     * One upload, true when the proxy took it. The init is tried PROXY_INIT_ATTEMPTS times with backoff, each attempt
     * with `deadlineMs`; a legacy segment once. A part (200 ms profile) once more after a retryable failure (a failed request, 429, 5xx,
     * NOT_ANNOUNCED), within the same deadline (never extended), while `mayRetry` allows it and at least
     * PROXY_PIPELINE_SLACK_MS (the round trips of a request and its answer) is left of it.
     */
    const send = async (deadlineMs: number, partSignal?: AbortSignal, mayRetry: () => boolean = edgeAllowsRetry): Promise<boolean> => {
      const attempts = part ? 2 : meta ? 1 : PROXY_INIT_ATTEMPTS;
      const deadlineAt = performance.now() + deadlineMs;
      let failure = '';
      let tries = 0;
      for (let attempt = 0; attempt < attempts && current(); attempt += 1) {
        if (attempt > 0 && part && (deadlineAt - performance.now() < PROXY_PIPELINE_SLACK_MS || !mayRetry())) break;
        if (attempt > 0 && !meta) await sleep(r, PROXY_INIT_RETRY_MS * 2 ** (attempt - 1));
        if (!current()) return false;
        let retryable = true;
        tries += 1;
        // A stalled upload must release the ordered queue while its broadcast stays live.
        const uploadAbort = new AbortController();
        const uploadDeadline = setTimeout(() => { observation?.finish('timeout'); uploadAbort.abort(new DOMException('Media upload timed out', 'TimeoutError')); },
          part ? Math.max(0, deadlineAt - performance.now()) : deadlineMs);
        try {
          const res = await doFetch(apiPath(r, ingest ? 'media' : `media/${segmentId}`), {
            method: 'PUT',
            headers: { ...authHeaders(r, false), ...headers },
            body: body instanceof Uint8Array ? new Uint8Array(body) : body,
            credentials: 'omit',
            signal: AbortSignal.any([r.abort.signal, proxyAbort.signal, uploadAbort.signal, ...(partSignal ? [partSignal] : [])]),
          });
          if (!current() || uploadAbort.signal.aborted || partSignal?.aborted) return false;
          if (res.ok) {
            if (current()) {
              lane.proxyPart = meta ?? null;
              if (!meta) lane.proxyInitReady = true;
            }
            if (!meta) lane.proxyInitResent = false;
            if (main && meta && r.keyId === keyId) {
              if (r.hidesSeed) {
                if (r.exposed) {
                  r.proxyStreak += 1;
                  if (!r.exposurePermanent && r.proxyStreak >= (r.transportProfile ? 25 : HIDDEN_PROXY_RECOVERY)) hideAgain(r);
                } else if (r.proxyFailures >= HIDDEN_PROXY_MISSES) {
                  store.set({ privacyWarning: null });
                }
              }
              r.proxyFailures = 0;
            }
            return true;
          }
          const code = await readErrorCode(res);
          if (!current()) return false;
          if (code === 'MEDIA_PROXY_IDLE') {
            setProxyActive(r, false, lane);
            return false;
          }
          if (code === 'MEDIA_PROXY_OFF' || code === 'MEDIA_PROXY_ENDED') {
            observation?.finish('failed');
            setProxyActive(r, false, lane);
            if (r.keyId === keyId) lane.mediaProxy = false;
            logger.log('info', SCOPE, `the ${main ? '' : 'compat '}media proxy of this broadcast is closed (${code}); segments go to the swarm only`);
            lost(code, true);
            return false;
          }
          failure = `answered ${res.status}${code ? ` ${code}` : ''}`;
          if (code === 'CAPTURED_AT_OUT_OF_RANGE') {
            // Every later segment would be refused the same way: the studio clock is off the server's.
            if (r.keyId === keyId) lane.mediaProxy = false;
            logger.log('warn', SCOPE, `the media proxy refused the capture time of ${segmentId}: this computer's clock is off; segments go to the swarm only`);
            lost(`${code}: this computer's clock is off`, true);
            return false;
          }
          // Compat resends the init its seed published, never one still being registered.
          const resend = main ? lane.init : (lane as CompatLane).publishedInit;
          if (code === 'INIT_REQUIRED' && resend) {
            // Once until an init gets through: an init the proxy keeps refusing is not sent for every segment.
            if (!lane.proxyInitResent) {
              lane.proxyInitResent = true;
              logger.log('warn', SCOPE, `the ${main ? '' : 'compat '}media proxy lost the init segment; sending it again`);
              sendToProxy(r, 'init.mp4', resend.bytes, keyId, undefined, lane);
            }
            return false;
          }
          retryable = res.status === 429 || res.status >= 500 || code === 'NOT_ANNOUNCED';
        } catch (err) {
          failure = partSignal?.aborted ? 'an earlier part of its GOP did not get through' : errorMessage(err);
          if (partSignal?.aborted) retryable = false;
        } finally {
          clearTimeout(uploadDeadline);
        }
        if (!retryable) break;
      }
      if (failure && current()) {
        // A part that failed at warn, with the server's code; the ones of its GOP given up with it, and a legacy
        // segment, at debug as before.
        const level = part && !partSignal?.aborted ? 'warn' : meta ? 'debug' : 'warn';
        logger.log(level, SCOPE, `${part && !main ? 'compat ' : ''}${segmentId} could not be sent to the media proxy` +
          `${part && tries > 1 ? ` (${tries} attempts)` : ''}: ${failure}`);
        if (meta) missed(failure);
        else lost(`the init segment ${failure}`);
      }
      return false;
    };

    lane.proxyPending += 1;
    lane.proxyPendingBytes += body.byteLength;
    let onTheWire = false;
    lane.proxyChain = lane.proxyChain.then(async () => {
      try {
        // An init, or a send without the pipeline, goes after everything put on the wire before it.
        if (!pipelined && lane.proxyInFlight.size > 0) await Promise.allSettled([...lane.proxyInFlight]);
        if (!meta) lane.proxySent = null;
        if (!current()) return;
        if (r.transportProfile && meta && !lane.proxyInitReady) { missed('waiting for the current init'); return; }
        if (!pipelined) {
          if (r.transportProfile && meta && !meta.keyframe &&
              (lane.proxyPart?.seq !== meta.seq-1 || lane.proxyPart?.gopStartSeq !== meta.gopStartSeq)) {
            missed('waiting for an independent part'); return;
          }
          const ok = await send(r.transportProfile && meta ? 600 : 5000);
          observation?.finish(ok ? (current() ? 'uploaded' : 'cancelled') : 'failed');
          return;
        }
        // Pipelined: a part goes on the wire once its predecessor of the same GOP did and has not failed; it does
        // not wait for that one's answer. The server stores them in order and refuses one after a gap (PART_GAP).
        const m = meta!;
        const independent = (): boolean => !!m.keyframe;
        const follows = (): boolean => {
          const sent = lane.proxySent;
          return !!sent && sent.seq === m.seq - 1 && sent.gopStartSeq === m.gopStartSeq && sent.gop.failedSeq === null;
        };
        if (!independent() && !follows()) { missed('waiting for an independent part'); return; }
        const previous = independent() ? null : lane.proxySent!;
        const gop: ProxyGop = previous?.gop ?? { failedSeq: null, onTheWire: new Map() };
        while (lane.proxyInFlight.size >= ingest!.maxInFlight) await Promise.race(lane.proxyInFlight);
        if (!current()) return;
        if (gop.failedSeq !== null) { missed('waiting for an independent part'); return; }
        const partMs = r.transportProfile!.partDurationMs;
        const own = pipelinedPartDeadlineMs(ingest!.maxInFlight, partMs, independent() ? body.byteLength : undefined);
        // Stored only after its predecessor, a dependent part cannot be answered before it: never given up before the
        // predecessor's deadline, plus a part's time for its own answer. (The server holds it as long as that one is
        // within its own bounds, so a healthy dependent is not refused for waiting.)
        // On the monotonic clock the timers run on: a wall clock step (NTP) must not move a deadline.
        const now = performance.now();
        const deadlineAt = Math.max(now + own, previous ? previous.deadlineAt + partMs : 0);
        const abort = new AbortController();
        gop.onTheWire.set(m.seq, abort);
        onTheWire = true;
        // Once more only while no earlier part of its GOP failed (else the server refuses it after the gap).
        const upload: Promise<void> = send(deadlineAt - now, abort.signal, () => edgeAllowsRetry() && (gop.failedSeq === null || gop.failedSeq > m.seq))
          .catch(() => false)
          .then((ok) => {
            observation?.finish(ok ? (current() ? 'uploaded' : 'cancelled') : 'failed');
            if (ok) return;
            // The parts after it cannot be decoded without it: not sent, and aborted if already on the wire. The
            // ones before it are whole and decodable: they carry on.
            if (gop.failedSeq === null || m.seq < gop.failedSeq) gop.failedSeq = m.seq;
            for (const [seq, later] of gop.onTheWire) if (seq > m.seq) later.abort();
          })
          .finally(() => {
            gop.onTheWire.delete(m.seq);
            lane.proxyInFlight.delete(upload);
            lane.proxyPending -= 1;
            lane.proxyPendingBytes -= body.byteLength;
          });
        lane.proxyInFlight.add(upload);
        lane.proxySent = { seq: m.seq, gopStartSeq: m.gopStartSeq, deadlineAt, gop };
      } finally {
        if (!onTheWire) {
          observation?.finish(current() ? 'skipped' : 'cancelled');
          lane.proxyPending -= 1;
          lane.proxyPendingBytes -= body.byteLength;
        }
      }
    }).catch(() => { observation?.finish('failed'); });
    return result;
  }

  /**
   * Resolves once everything queued for the proxy on `lane` was sent and answered (or given up), including what was
   * queued meanwhile (an init sent again after INIT_REQUIRED); gives up after PROXY_FLUSH_TIMEOUT_MS.
   */
  async function proxyDrained(lane: ProxyLane): Promise<void> {
    const until = performance.now() + PROXY_FLUSH_TIMEOUT_MS;
    for (;;) {
      const chain = lane.proxyChain;
      await chain;
      await Promise.allSettled([...lane.proxyInFlight]);
      if ((lane.proxyChain === chain && lane.proxyInFlight.size === 0) || performance.now() >= until) return;
    }
  }

  /** Resolves after `ms`, or at once when the run is aborted. */
  function sleep(r: Run, ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      const timer = setTimeout(done, ms);
      function done(): void {
        clearTimeout(timer);
        r.abort.signal.removeEventListener('abort', done);
        resolve();
      }
      r.abort.signal.addEventListener('abort', done);
    });
  }

  function makeSink(r: Run): EncodedPacketSink {
    return {
      onInit(bytes, info, laneId) {
        if (laneId === COMPAT_RENDITION_ID) { onCompatInit(r, bytes, info); return; }
        if (r.transportProfile && info.codecs !== r.preflight?.codecs) {
          fail(r, 'The encoder changed the broadcast codec or did not provide AAC audio. Start a new broadcast.'); return;
        }
        if (run === r && r.init) invalidatePublication(r);
        r.codecs = info.codecs;
        r.init = { bytes, codecs: info.codecs };
        if (info.codecs !== GO_LIVE_CODECS_GUESS) {
          logger.log('debug', SCOPE, `exact codecs ${info.codecs} differ from the go-live guess ${GO_LIVE_CODECS_GUESS}`);
        }
        // The init segment carries no media and is published in clear: viewers need it before the key.
        enqueue(r, {
          kind: 'other',
          label: 'init segment',
          run: async () => {
            if (!r.engine) return;
            await r.engine.publishInit(bytes, { codecs: info.codecs });
            r.publishedPart = null;
            sendToProxy(r, 'init.mp4', bytes, r.keyId);
          },
        });
      },
      onSegment(seg, laneId) {
        if (laneId === COMPAT_RENDITION_ID) { onCompatSegment(r, seg); return; }
        if (seg.durationMs > 0) {
          r.encodedSamples.push({ bytes: seg.bytes.length, durationMs: seg.durationMs });
          if (r.encodedSamples.length > 4) r.encodedSamples.shift();
          r.lastEncodedAt = Date.now();
        }
        const selectedWitness = witness;
        const selectedRuntime=runtimePublication;
        const runtimeSelected=selectedRuntime?.select(seg);
        const runtimeBytes = runtimeSelected ? selectedRuntime?.witnessBytes(seg) : undefined;
        const witnessedBytes = selectedWitness?.select(seg);
        // Capture the proxy interval before encryption/publishing can yield. Idle or retired work
        // must not become an upload when demand returns while this queue item is still running.
        const proxySignal = r.proxyAbort.signal;
        const generation = r.publicationGeneration;
        // Kept in clear for the clips the control plane asks for (see clip-requests.ts), unless clips are off.
        if (r.options.clips !== false) clipsOf(r).onSegment(seg);
        enqueue(r, {
          kind: 'segment',
          segment: seg,
          label: `segment ${seg.seq}`,
          run: async () => {
            const engine = r.engine;
            const key = r.key;
            const keyId = r.keyId;
            const broadcastId = r.broadcastId;
            if (!engine || !key) {
              // Between sessions (re-go-live): there is nobody to publish to.
              r.droppedSegments += 1;
              return;
            }
            if (r.transportProfile && !seg.keyframe &&
                (r.publishedPart?.seq !== seg.seq-1 || r.publishedPart?.gopStartSeq !== seg.gopStartSeq)) {
              r.droppedSegments++; return;
            }
            // seq comes from the segmenter (time-derived base); it is never assumed to start at 0.
            // AAD binds the bytes to the go-live key id and segment id, as the engine decrypts them.
            let plain = witnessedBytes ? await witnessedBytes() : runtimeBytes ? await runtimeBytes() : seg.bytes;
            if (witnessedBytes && !selectedWitness?.current()) plain = seg.bytes;
            const ciphertext = await aesGcmEncrypt(key, plain, segmentAad(keyId, segmentIdForSeq(seg.seq)));
            if (witnessedBytes) selectedWitness?.current();
            const sha256 = await sha256Hex(ciphertext);
            if (witnessedBytes) selectedWitness?.current();
            await engine.publishSegment({
              seq: seg.seq,
              durationMs: seg.durationMs,
              capturedAt: seg.capturedAt,
              independent: seg.keyframe,
              gopStartSeq: seg.gopStartSeq ?? seg.seq,
              ciphertext,
              sha256,
              keyId,
            });
            r.publishedPart = seg;
            const upload = sendToProxy(r, segmentIdForSeq(seg.seq), ciphertext, keyId, seg, r, { sha256, generation, broadcastId, engine, proxySignal });
            if (witnessedBytes && plain !== seg.bytes) selectedWitness?.observe(upload, {
              streamId: r.streamId, broadcastId, keyId, generation, renditionId: 'main',
              segmentId: segmentIdForSeq(seg.seq), seq: seg.seq, sha256,
            });
            if(runtimeSelected&&(!runtimeBytes||plain!==seg.bytes))selectedRuntime?.observe(upload,{
              streamId:r.streamId,broadcastId,keyId,generation,renditionId:'main',segmentId:segmentIdForSeq(seg.seq),seq:seg.seq,sha256,
            });
            r.liveSeq = seg.seq;
            r.lastCapturedAt = seg.capturedAt;
          },
        });
      },
      onError(e) {
        fail(r, `The encoder stopped: ${e.message}`);
      },
      onLaneFailed(laneId, e) {
        if (laneId === COMPAT_RENDITION_ID && r.compat) shedCompat(r, r.compat, `its encoder stopped: ${e.message}`);
      },
    };
  }

  // ---- H.264 fallback lane --------------------------------------------------------------------
  //
  // Order per init: the compat init is registered (PUT renditions/compat, which hands out the compat seed token)
  // before the compat seed joins the tracker, then published on that seed, then sent to compat's media proxy. A new
  // init (an encoder re-init, the 480p step) registers again: a new token, so a new seed engine. Nothing here ever
  // fails or re-goes-live the broadcast: whatever goes wrong ends the lane, and main carries on.

  /** A lane for this go-live's `fallback` answer (also after a re-go-live, keeping the encoder's current init). */
  function compatSession(r: Run, live: GoLive, fallback: CompatGoLive, previous: CompatLane | null): CompatLane {
    const plan = r.compatPlan!;
    return {
      state: 'starting', reason: null, video: previous?.video ?? plan.video, codecs: plan.codecs,
      transportProfile: fallback.transportProfile, generation: live.keyId, streamId: live.streamId,
      signalingUrl: live.signalingUrl, seed: { seedToken: fallback.seedToken, seedPeerId: fallback.seedPeerId, ts: fallback.ts },
      engine: null, init: previous?.init ?? null, publishedInit: null, registeredSha: null,
      lastRegisterAt: previous?.lastRegisterAt ?? 0, syncing: null, resync: false, publishedPart: null,
      // A fresh publish chain: parts still queued on the previous session's find no seed for their init and are dropped.
      chain: Promise.resolve(), pending: 0, droppedSegments: previous?.droppedSegments ?? 0,
      encodedSamples: previous?.encodedSamples ?? [], lastEncodedAt: previous?.lastEncodedAt ?? null,
      pollAfterMs: fallback.mediaProxy.pollAfterMs, retiring: null, rejoins: 0,
      mediaProxy: fallback.mediaProxy.enabled, proxyActive: fallback.mediaProxy.active, proxyAbort: new AbortController(),
      proxyChain: Promise.resolve(), proxyPending: 0, proxyPendingBytes: 0, proxyInitRevision: 0, proxyInitReady: false,
      proxyInitResent: false, proxyPart: null, proxyInFlight: new Set(), proxySent: null,
    };
  }

  /** Whether `c` is still the run's live (or starting) lane of the go-live it was made for. */
  const compatCurrent = (r: Run, c: CompatLane): boolean =>
    run === r && !r.closed && !r.reliving && r.compat === c && !compatGone(c) && r.keyId === c.generation;

  function onCompatInit(r: Run, bytes: Uint8Array, info: InitInfo): void {
    const c = r.compat;
    if (!c || compatGone(c)) return;
    if (info.codecs !== c.codecs) {
      retireCompat(r, c, 'unavailable', `the H.264 encoder produced ${info.codecs}, not the declared ${c.codecs}`, true);
      return;
    }
    if (run === r && c.init) invalidatePublication(r);
    const init: NonNullable<CompatLane['init']> = { bytes, codecs: info.codecs, sha256: null };
    c.init = init;
    void sha256Hex(bytes).then((sha) => {
      init.sha256 = sha;
      if (r.compat === c && c.init === init) syncCompat(r, c);
    });
  }

  /** Registers the lane's current init and seeds it, once at a time; a newer init while it runs goes next. */
  function syncCompat(r: Run, c: CompatLane): void {
    if (c.syncing) { c.resync = true; return; }
    c.syncing = (async () => {
      try {
        do {
          c.resync = false;
          await registerAndSeed(r, c);
        } while (c.resync && compatCurrent(r, c));
      } catch (err) {
        if (!compatCurrent(r, c)) return;
        const unavailable = err instanceof CompatUnavailable;
        retireCompat(r, c, unavailable ? 'unavailable' : 'shed', errorMessage(err), unavailable ? err.withdraw : true);
      } finally {
        c.syncing = null;
      }
    })();
  }

  async function registerAndSeed(r: Run, c: CompatLane): Promise<void> {
    const init = c.init;
    if (!init?.sha256 || !compatCurrent(r, c) || (c.registeredSha === init.sha256 && c.engine)) return;
    // Init changes are debounced: the backend takes 20 registrations a minute per stream.
    const wait = c.lastRegisterAt + COMPAT_REGISTER_SPACING_MS - Date.now();
    if (wait > 0) await sleep(r, wait);
    if (!compatCurrent(r, c) || c.init !== init) { c.resync = true; return; }
    const seed = await registerCompat(r, c, init.sha256);
    c.lastRegisterAt = Date.now();
    if (!compatCurrent(r, c)) return;
    const rotated = seed.seedToken !== c.seed.seedToken || seed.ts !== c.seed.ts || !c.engine;
    c.seed = seed;
    c.registeredSha = init.sha256;
    if (rotated) {
      // A new token closes the old compat seed at the tracker anyway (and its viewers, who join the new init).
      const old = c.engine;
      c.engine = null;
      c.publishedInit = null;
      destroyEngine(old);
      const engine = await connectCompat(r, c);
      if (!compatCurrent(r, c)) { destroyEngine(engine); return; }
      c.engine = engine;
    }
    await c.engine!.publishInit(init.bytes, { codecs: init.codecs });
    if (!compatCurrent(r, c)) return;
    c.publishedInit = init;
    c.publishedPart = null;
    c.state = 'live';
    logger.log('info', SCOPE, `H.264 fallback live at ${c.video.width}x${c.video.height} as ${c.seed.seedPeerId}`);
    sendToProxy(r, 'init.mp4', init.bytes, c.generation, undefined, c);
  }

  /** PUT renditions/compat: the compat seed's credentials for this init. Throws CompatUnavailable when it cannot be had. */
  async function registerCompat(r: Run, c: CompatLane, initSha256: string): Promise<CompatLane['seed']> {
    let failure = '';
    for (let attempt = 0; attempt < COMPAT_REGISTER_ATTEMPTS; attempt += 1) {
      let res: Response;
      try {
        res = await doFetch(apiPath(r, 'renditions/compat'), {
          method: 'PUT', headers: authHeaders(r, true), body: JSON.stringify({ generation: c.generation, initSha256 }),
          credentials: 'omit', cache: 'no-store', signal: r.abort.signal,
        });
      } catch (err) {
        if (!compatCurrent(r, c)) throw err;
        failure = errorMessage(err);
        await sleep(r, backoffDelay(attempt));
        continue;
      }
      if (res.ok) {
        const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
        if (body && typeof body.seedToken === 'string' && body.seedToken && typeof body.seedPeerId === 'string' && body.seedPeerId &&
            typeof body.ts === 'number') {
          return { seedToken: body.seedToken, seedPeerId: body.seedPeerId, ts: body.ts };
        }
        throw new CompatUnavailable('the server answered the compat registration without seed credentials', true);
      }
      if (res.status === 429) {
        failure = 'rate limited (429)';
        await sleep(r, await retryAfterOf(res));
        continue;
      }
      if (res.status >= 500) {
        failure = `answered ${res.status}`;
        await sleep(r, backoffDelay(attempt));
        continue;
      }
      const code = await readErrorCode(res);
      // After a backend restart the declaration is gone (COMPAT_NOT_DECLARED) or the stream is not live: nothing to withdraw.
      const gone = code === 'COMPAT_NOT_DECLARED' || code === 'STREAM_NOT_LIVE' || res.status === 404;
      throw new CompatUnavailable(`the server refused the compat registration (${res.status}${code ? ` ${code}` : ''})`, !gone);
    }
    throw new CompatUnavailable(`the compat registration kept failing: ${failure}`, true);
  }

  /** Retry-After of a 429: the body's retryAfterMs (cross-origin pages cannot read the header), else the header, else 3 s. */
  async function retryAfterOf(res: Response): Promise<number> {
    const header = Number(res.headers.get('Retry-After'));
    const body = await readRetryAfterMs(res.clone());
    return Math.min(60_000, body ?? (Number.isFinite(header) && header > 0 ? header * 1000 : COMPAT_REGISTER_SPACING_MS));
  }

  /** Compat seed engine, joined to the `compat` swarm with the token of the latest registration. Shares main's ICE. */
  async function connectCompat(r: Run, c: CompatLane): Promise<SwarmEngine> {
    const ice = r.ice ?? { stunUrls: ['stun:stun.l.google.com:19302'], turnServers: [] };
    const seed = c.seed;
    const engine = makeEngine({
      signalingUrl: withRendition(c.signalingUrl, COMPAT_RENDITION_ID),
      role: 'seed',
      peerId: seed.seedPeerId,
      seedToken: seed.seedToken,
      signalingTokenTs: seed.ts,
      getSignalingToken: async () => ({ token: seed.seedToken, ts: seed.ts }),
      signalingTokenStreamId: c.streamId,
      keyId: c.generation,
      segmentDurationMs: r.options.segmentMs,
      transportProfile: c.transportProfile,
      transportCapabilities: { transportVersions: [1, 2], videoCodecs: ['h264'] },
      // Hidden with main: compat viewers then get it from compat's media proxy only.
      ...(r.hidesSeed ? { acceptPeerConnections: false, seedFirstHopFanout: 0 } : { seedFirstHopFanout: COMPAT_SEED_FANOUT }),
      ...(ice.stunUrls.length > 0 ? { stunUrls: ice.stunUrls } : {}),
      ...(ice.turnServers.length > 0 ? { turnServers: ice.turnServers } : {}),
    });
    let joined = false;
    let rejectJoin: (err: Error) => void = () => {};
    const refused = new Promise<never>((_, reject) => { rejectJoin = reject; });
    refused.catch(() => undefined);
    engine.on?.('signaling-rejected', (info) => {
      if (info.willRetry === true && terminalRejection(info.code) === null) return;
      if (!joined) { rejectJoin(new Error(`the tracker refused the compat seed (${info.code} ${info.reason})`)); return; }
      // Never a re-go-live for the fallback: rejoined once, or shed.
      if (r.compat === c && c.engine === engine) onCompatRejected(r, c, engine, info);
    });
    try {
      const cancelled = new Promise<never>((_, reject) => {
        const onCancel = (): void => reject(new Superseded());
        if (r.cancel.signal.aborted) onCancel();
        else r.cancel.signal.addEventListener('abort', onCancel, { once: true });
      });
      cancelled.catch(() => undefined);
      await Promise.race([
        withTimeout(engine.join(c.streamId, COMPAT_RENDITION_ID), JOIN_TIMEOUT_MS, 'the compat seed could not join the tracker in time'),
        refused,
        cancelled,
      ]);
    } catch (err) {
      destroyEngine(engine);
      throw err;
    }
    joined = true;
    return engine;
  }

  /**
   * The tracker closed the compat seed. 4409/4410 are final: shed. Anything else (4401 after a tracker or backend
   * restart, 4408) is often main's trouble too: the seed goes, the lane waits a moment so that main's re-go-live
   * (which re-registers compat for its new generation) takes over, and otherwise registers and joins again, once per
   * session. A second rejection in the same session sheds it.
   */
  function onCompatRejected(r: Run, c: CompatLane, engine: SwarmEngine, info: SignalingRejection): void {
    const why = `the tracker closed the compat seed (${info.code} ${info.reason})`;
    if (terminalRejection(info.code) !== null || c.rejoins >= 1) { shedCompat(r, c, why); return; }
    c.rejoins += 1;
    c.engine = null;
    c.publishedInit = null;
    c.registeredSha = null;
    c.state = 'starting';
    destroyEngine(engine);
    logger.log('warn', SCOPE, `${why}; the H.264 fallback joins again`);
    void sleep(r, COMPAT_REJOIN_DELAY_MS).then(() => {
      if (compatCurrent(r, c)) syncCompat(r, c);
    });
  }

  function onCompatSegment(r: Run, seg: MediaSegment): void {
    const c = r.compat;
    if (!c || compatGone(c)) return;
    if (seg.durationMs > 0) {
      c.encodedSamples.push({ bytes: seg.bytes.length, durationMs: seg.durationMs });
      if (c.encodedSamples.length > 4) c.encodedSamples.shift();
      c.lastEncodedAt = Date.now();
    }
    if (c.pending >= COMPAT_MAX_PENDING) { c.droppedSegments += 1; return; }
    // The init this part belongs to: it is only published on a seed that published that init.
    const init = c.init;
    const proxySignal = c.proxyAbort.signal;
    const generation = r.publicationGeneration;
    c.pending += 1;
    c.chain = c.chain.then(async () => {
      try {
        const lane = r.compat;
        if (!lane || compatGone(lane)) return;
        const engine = lane.engine, key = r.key, keyId = r.keyId, broadcastId = r.broadcastId;
        if (!engine || !key || lane.generation !== keyId || lane.publishedInit !== init || !init) { lane.droppedSegments += 1; return; }
        if (!seg.keyframe && (lane.publishedPart?.seq !== seg.seq - 1 || lane.publishedPart?.gopStartSeq !== seg.gopStartSeq)) {
          lane.droppedSegments += 1; return;
        }
        const segmentId = segmentIdForSeq(seg.seq);
        // protocolVersion 2: the rendition id is part of the AES-GCM context (not segmentAad).
        const ciphertext = await aesGcmEncrypt(key, seg.bytes, compatAad(keyId, segmentId));
        const sha256 = await sha256Hex(ciphertext);
        if (lane.engine !== engine || compatGone(lane)) return;
        await engine.publishSegment({
          seq: seg.seq, durationMs: seg.durationMs, capturedAt: seg.capturedAt, independent: seg.keyframe,
          gopStartSeq: seg.gopStartSeq ?? seg.seq, ciphertext, sha256, keyId,
        });
        lane.publishedPart = seg;
        sendToProxy(r, segmentId, ciphertext, keyId, seg, lane, { sha256, generation, broadcastId, engine, proxySignal });
      } catch (err) {
        logger.log('debug', SCOPE, `compat segment ${seg.seq} could not be published: ${errorMessage(err)}`);
      } finally {
        c.pending -= 1;
      }
    });
  }

  function shedCompat(r: Run, c: CompatLane, why: string): void {
    retireCompat(r, c, 'shed', why, true);
  }

  /**
   * Ends the fallback for the rest of this broadcast: the segmenter stops encoding it, its seed tells the tracker
   * `stream-ended` (compat viewers get 4410, not 4401), then DELETE renditions/compat withdraws it, then the seed goes.
   */
  function retireCompat(r: Run, c: CompatLane, state: 'shed' | 'unavailable', why: string, withdraw: boolean): void {
    if (compatGone(c)) return;
    c.state = state;
    c.reason = why;
    logger.log('warn', SCOPE, `H.264 fallback ${state === 'shed' ? 'stopped' : 'unavailable'} for this broadcast: ${why}. Main carries on.`);
    try { r.segmenter?.dropLane?.(COMPAT_RENDITION_ID); } catch (err) { logger.log('debug', SCOPE, `dropping the compat lane failed: ${errorMessage(err)}`); }
    c.proxyAbort.abort();
    const engine = c.engine;
    c.engine = null;
    c.publishedInit = null;
    if (engine) {
      try { engine.endStream('compat-withdrawn'); } catch (err) { logger.log('debug', SCOPE, `compat endStream failed: ${errorMessage(err)}`); }
    }
    c.retiring = (async () => {
      if (withdraw && !r.closed) await withdrawCompat(r, c);
      destroyEngine(engine);
    })();
    const stats = run === r ? readStats(r) : null;
    if (stats) store.set({ stats });
  }

  /** DELETE renditions/compat; a 429 waits its Retry-After once. Best effort: a backend restart has forgotten it anyway. */
  async function withdrawCompat(r: Run, c: CompatLane): Promise<void> {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const res = await doFetch(apiPath(r, 'renditions/compat'), {
          method: 'DELETE', headers: authHeaders(r, true), body: JSON.stringify({ generation: c.generation }),
          credentials: 'omit', cache: 'no-store', signal: AbortSignal.any([r.abort.signal, AbortSignal.timeout(END_TIMEOUT_MS)]),
        });
        if (res.status === 429 && attempt === 0) { await sleep(r, await retryAfterOf(res)); continue; }
        if (!res.ok) logger.log('debug', SCOPE, `withdrawing the H.264 fallback answered ${res.status}`);
        return;
      } catch (err) {
        logger.log('debug', SCOPE, `could not withdraw the H.264 fallback: ${errorMessage(err)}`);
        return;
      }
    }
  }

  async function postEnd(r: Run): Promise<void> {
    if (!r.wentLive) return;
    r.wentLive = false;
    try {
      const res = await doFetch(apiPath(r, 'end'), {
        method: 'POST',
        headers: authHeaders(r, true),
        // The backend ignores (409) an end whose keyId is not the current go-live session's, so an
        // end that arrives late can never end a newer broadcast.
        body: JSON.stringify(r.liveKeyId ? { keyId: r.liveKeyId } : {}),
        credentials: 'omit',
        // Lets the request outlive the page when stop() runs during unload.
        keepalive: true,
        signal: AbortSignal.timeout(END_TIMEOUT_MS),
      });
      if (res.status === 409) logger.log('debug', SCOPE, 'the backend ignored a stale end: a newer go-live is current');
      else if (!res.ok) logger.log('warn', SCOPE, `ending the stream answered ${res.status}`);
    } catch (err) {
      logger.log('warn', SCOPE, `could not end the stream: ${errorMessage(err)}`);
    }
  }

  function clearTimers(r: Run): void {
    if (r.heartbeatTimer) clearTimeout(r.heartbeatTimer);
    if (r.thumbnailTimer) clearTimeout(r.thumbnailTimer);
    if (r.statsTimer) clearInterval(r.statsTimer);
    if (r.reliveTimer) clearTimeout(r.reliveTimer);
    r.heartbeatTimer = r.thumbnailTimer = r.statsTimer = r.reliveTimer = null;
  }

  /**
   * Flushes the last segment (when `flush`), tells the swarm and the control plane the stream is
   * over and frees everything. Safe to call more than once.
   */
  async function teardown(r: Run, flush: boolean): Promise<void> {
    clearTimers(r);
    const segmenter = r.segmenter;
    r.segmenter = null;
    if (segmenter) {
      try {
        // stop() emits the final, possibly short, segment through the sink.
        if (flush) await segmenter.stop();
        else void segmenter.stop().catch(() => undefined);
      } catch (err) {
        logger.log('warn', SCOPE, `segmenter stop failed: ${errorMessage(err)}`);
      }
    }
    if (flush) {
      await drain(r);
      // The last segment reaches the media proxy too, unless the proxy is slow to take it.
      await withTimeout(proxyDrained(r), PROXY_FLUSH_TIMEOUT_MS, 'the media proxy did not take the last segment in time').catch(() => undefined);
      const c = r.compat;
      if (c) {
        await withTimeout(c.chain.then(() => proxyDrained(c)), PROXY_FLUSH_TIMEOUT_MS, 'the compat lane did not flush in time').catch(() => undefined);
      }
    }
    r.closed = true;
    r.queue.length = 0;
    const compat = r.compat;
    if (compat) {
      compat.proxyAbort.abort();
      const engine = compat.engine;
      compat.engine = null;
      if (engine) {
        try {
          engine.endStream('studio-stopped');
        } catch (err) {
          logger.log('debug', SCOPE, `compat endStream failed: ${errorMessage(err)}`);
        }
        destroyEngine(engine);
      }
      if (compat.retiring) await withTimeout(compat.retiring, END_TIMEOUT_MS, 'withdrawing the fallback timed out').catch(() => undefined);
    }
    // A session joined during a re-go-live but not yet installed is still a live seed connection.
    const pending = r.pendingEngine;
    r.pendingEngine = null;
    destroyEngine(pending);
    const engine = r.engine;
    r.engine = null;
    if (engine) {
      try {
        engine.endStream('studio-stopped');
      } catch (err) {
        logger.log('debug', SCOPE, `endStream failed: ${errorMessage(err)}`);
      }
      destroyEngine(engine);
    }
    r.key = null;
    await postEnd(r);
    r.abort.abort();
  }

  function fail(r: Run, message: string): void {
    if (run !== r) return;
    run = null;
    logger.log('error', SCOPE, message);
    store.set({ ...IDLE, status: 'error', error: message, reconnectAttempts: store.get().reconnectAttempts });
    r.cancel.abort();
    void retire(async () => {
      await teardown(r, false);
      await r.reliveDone;
    });
  }

  function scheduleHeartbeat(r: Run, delay: number): void {
    if (r.heartbeatTimer) clearTimeout(r.heartbeatTimer);
    r.heartbeatTimer = setTimeout(() => {
      r.heartbeatTimer = null;
      void heartbeat(r);
    }, delay);
  }

  function setProxyActive(r: Run, active: boolean, lane: ProxyLane = r): void {
    if (lane.proxyActive === active) return;
    lane.proxyAbort.abort();
    lane.proxyAbort = new AbortController();
    lane.proxyActive = active;
    lane.proxyInitResent = false;
    if (lane === r) {
      if (active && r.init) sendToProxy(r, 'init.mp4', r.init.bytes, r.keyId);
      return;
    }
    // Compat's init only once it is registered and published on its seed (see syncCompat).
    const c = lane as CompatLane;
    if (active && c.publishedInit && c.publishedInit === c.init) sendToProxy(r, 'init.mp4', c.publishedInit.bytes, c.generation, undefined, c);
  }

  function applyProxyDemand(r: Run, body: unknown): void {
    if (!body || typeof body !== 'object') return;
    const policy = (body as { mediaProxy?: { enabled?: unknown; active?: unknown; pollAfterMs?: unknown; compat?: unknown } }).mediaProxy;
    // Compat's proxy follows its own viewers (`mediaProxy.compat`), whatever main's does.
    if (r.compat && !compatGone(r.compat) && policy && typeof policy === 'object') applyCompatDemand(r, r.compat, policy.compat);
    if (!policy || typeof policy.active !== 'boolean' || typeof policy.enabled !== 'boolean') return;
    // An ended proxy cannot be recreated mid-broadcast.
    if (!r.mediaProxy) return;
    if (policy.enabled === false) {
      r.mediaProxy = false;
      r.proxyPollAfterMs = HEARTBEAT_INTERVAL_MS;
    }
    setProxyActive(r, policy.enabled && policy.active);
    if (policy.enabled && typeof policy.pollAfterMs === 'number' && Number.isFinite(policy.pollAfterMs)) {
      r.proxyPollAfterMs = Math.max(1000, Math.min(HEARTBEAT_INTERVAL_MS, policy.pollAfterMs));
    }
  }

  function applyCompatDemand(r: Run, c: CompatLane, value: unknown): void {
    if (!value || typeof value !== 'object' || !c.mediaProxy) return;
    const p = value as { enabled?: unknown; active?: unknown; pollAfterMs?: unknown };
    if (typeof p.enabled !== 'boolean' || typeof p.active !== 'boolean') return;
    if (!p.enabled) c.mediaProxy = false;
    setProxyActive(r, p.enabled && p.active, c);
    if (p.enabled && typeof p.pollAfterMs === 'number' && Number.isFinite(p.pollAfterMs)) {
      c.pollAfterMs = Math.max(1000, Math.min(HEARTBEAT_INTERVAL_MS, p.pollAfterMs));
    }
  }

  /** How soon the next heartbeat asks for proxy demand: main's poll, or compat's when its proxy is open. */
  const pollAfter = (r: Run): number =>
    r.compat && !compatGone(r.compat) && r.compat.mediaProxy ? Math.min(r.proxyPollAfterMs, r.compat.pollAfterMs) : r.proxyPollAfterMs;

  async function heartbeat(r: Run): Promise<void> {
    if (run !== r || r.reliving) return;
    const keyId = r.keyId;
    let failure: SwarmFailure;
    try {
      // Latest sampled figures for the portal's analytics; a backend that ignores the body still answers.
      const stats = store.get().stats;
      const res = await doFetch(apiPath(r, 'heartbeat'), {
        method: 'PUT',
        headers: authHeaders(r, true),
        body: JSON.stringify({ uploadKbps: stats?.uploadKbps ?? 0, droppedFrames: stats?.droppedFrames ?? 0 }),
        credentials: 'omit',
        cache: 'no-store',
        signal: r.abort.signal,
      });
      if (run !== r || r.reliving || r.keyId !== keyId) return;
      if (res.ok) {
        const answer = res.clone();
        const status = await readStreamStatus(res);
        if (run !== r || r.reliving || r.keyId !== keyId) return;
        // After a backend restart the stream comes back as 'created'; after the seed grace it is
        // 'offline'. Either way this session's seed token is dead and nobody can watch.
        // A deliberate end never lands here: the tracker's 4410 arrives first and stops the studio, so
        // 'offline' while still running means the seed timed out (transient loss) and re-go-live is right.
        if (status !== undefined && status !== 'live' && status !== 'starting') {
          relive(r, `ompeer.com reports the stream as "${status}"`);
          return;
        }
        r.heartbeatFailures = 0;
        store.set({ status: 'live', reconnectAttempts: 0, error: null });
        // Clips asked for through the API arrive with this answer; while one is owed the next heartbeat comes sooner.
        const body: unknown = await answer.json().catch(() => null);
        if (run !== r || r.reliving || r.keyId !== keyId) return;
        applyProxyDemand(r, body);
        const owed = r.options.clips === false ? undefined : clipsOf(r).onHeartbeat(body);
        if (run !== r || r.reliving || r.keyId !== keyId) return;
        scheduleHeartbeat(r, Math.min(owed ?? HEARTBEAT_INTERVAL_MS, pollAfter(r)));
        return;
      }
      if (res.status === 401 || res.status === 404) {
        relive(r, `the heartbeat was refused (${res.status})`);
        return;
      }
      failure = describeSwarmFailure(res.status, await readErrorCode(res));
    } catch (err) {
      if (run !== r || r.reliving || r.keyId !== keyId) return;
      failure = SWARM_NETWORK_FAILURE;
      logger.log('debug', SCOPE, `heartbeat failed: ${errorMessage(err)}`);
    }
    if (r.keyId !== keyId) return;
    if (!failure.retryable) {
      fail(r, failure.message);
      return;
    }
    // The swarm keeps flowing while the control plane is unreachable; keep trying sooner than usual.
    const delay = Math.min(HEARTBEAT_INTERVAL_MS, backoffDelay(r.heartbeatFailures));
    r.heartbeatFailures += 1;
    logger.log('warn', SCOPE, `${failure.message} Heartbeat retry in ${delay} ms (attempt ${r.heartbeatFailures}).`);
    store.set({ status: 'reconnecting', reconnectAttempts: r.heartbeatFailures });
    scheduleHeartbeat(r, delay);
  }

  function scheduleThumbnail(r: Run, delay: number): void {
    if (r.thumbnailTimer) clearTimeout(r.thumbnailTimer);
    r.thumbnailTimer = setTimeout(() => {
      r.thumbnailTimer = null;
      void thumbnail(r);
    }, delay);
  }

  /**
   * Best effort: the directory shows a stale or missing picture when this fails, and nothing else
   * changes. No failure here touches the state, the heartbeat or the session.
   */
  async function thumbnail(r: Run): Promise<void> {
    if (run !== r || r.reliving) return;
    scheduleThumbnail(r, THUMBNAIL_INTERVAL_MS);
    if (r.thumbnailBusy) return;
    r.thumbnailBusy = true;
    try {
      const image = await grabThumbnail(r.options.stream);
      if (!image || run !== r || r.reliving) return;
      const res = await doFetch(apiPath(r, 'thumbnail'), {
        method: 'PUT',
        headers: authHeaders(r, false),
        body: image,
        credentials: 'omit',
        cache: 'no-store',
        signal: r.abort.signal,
      });
      if (res.ok) return;
      logger.log('debug', SCOPE, `thumbnail upload answered ${res.status}`);
      if (res.status !== 429) return;
      // Retry-After is not exposed to cross-origin pages; the body carries the same wait.
      const retryAfterMs = await readRetryAfterMs(res);
      if (run !== r || r.reliving || retryAfterMs === undefined || retryAfterMs >= THUMBNAIL_INTERVAL_MS) return;
      scheduleThumbnail(r, Math.max(THUMBNAIL_MIN_RETRY_MS, retryAfterMs));
    } catch (err) {
      logger.log('debug', SCOPE, `thumbnail failed: ${errorMessage(err)}`);
    } finally {
      r.thumbnailBusy = false;
    }
  }

  async function readRetryAfterMs(res: Response): Promise<number | undefined> {
    try {
      const body = (await res.json()) as { retryAfterMs?: unknown };
      return typeof body.retryAfterMs === 'number' && body.retryAfterMs > 0 ? body.retryAfterMs : undefined;
    } catch {
      return undefined;
    }
  }

  async function readStreamStatus(res: Response): Promise<string | undefined> {
    try {
      const body = (await res.json()) as { status?: unknown };
      return typeof body.status === 'string' ? body.status : undefined;
    } catch {
      return undefined;
    }
  }

  async function readErrorCode(res: Response): Promise<string | undefined> {
    try {
      const body = (await res.json()) as { code?: unknown };
      return typeof body.code === 'string' ? body.code : undefined;
    } catch {
      return undefined;
    }
  }

  async function withTimeout<T>(work: Promise<T>, ms: number, message: string): Promise<T> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        work,
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => reject(new SwarmError(message, true)), ms);
        }),
      ]);
    } finally {
      clearTimeout(timer);
    }
  }

  /** The broadcast runs AV1 only from here on: no fallback in this or a later go-live; the stats say why. */
  function dropCompatPlan(r: Run, why: string): void {
    r.compatPlan = null;
    r.compatNote = why;
    if (r.compat && !compatGone(r.compat)) retireCompat(r, r.compat, 'unavailable', why, false);
    logger.log('warn', SCOPE, `publishing AV1 only: ${why}`);
  }

  /** One go-live call: a new key, key id and seed credentials. Throws SwarmError or Superseded. */
  async function goLive(r: Run): Promise<{ live: GoLive; key: CryptoKey }> {
    const alive = (): void => {
      if (run !== r) throw new Superseded();
    };
    const body: Record<string, unknown> = { codecs: r.preflight?.codecs ?? GO_LIVE_CODECS_GUESS, segmentDurationMs: r.options.segmentMs };
    if (r.transportProfile) body.transportProfile = r.transportProfile;
    // A title is sent once: with the go-live it was given for, or after an edit the control plane did not take. A
    // re-go-live after a drop sends none, so the title the channel has by then (it may have been changed in the
    // portal) stays.
    const title = r.options.title;
    if (title) body.title = title;
    if (r.options.category) body.category = r.options.category;
    if (r.options.tags && r.options.tags.length > 0) body.tags = r.options.tags;
    if (r.options.language) body.language = r.options.language;
    body.contentLabels = r.options.contentLabels ?? [];
    if (r.options.disconnectGraceMs !== undefined) body.disconnectGraceMs = r.options.disconnectGraceMs;
    // This studio sends every segment to the media proxy when there is one: the backend may then hide it from viewers.
    body.feedsMediaProxy = true;
    // The H.264 fallback, unless it already ended in this broadcast (a re-go-live never brings a shed lane back).
    const plan = r.compatPlan;
    if (plan && !(r.compat && compatGone(r.compat))) {
      const fallback = fallbackRequest(r, plan, r.compat?.video ?? plan.video);
      if (typeof fallback === 'string') dropCompatPlan(r, `the fallback was not offered: ${fallback}`);
      else body.fallback = fallback;
    }
    let res: Response;
    try {
      res = await doFetch(apiPath(r, 'go-live'), {
        method: 'POST',
        headers: authHeaders(r, true),
        body: JSON.stringify(body),
        credentials: 'omit',
        cache: 'no-store',
        signal: r.abort.signal,
      });
    } catch {
      alive();
      throw new SwarmError(SWARM_NETWORK_FAILURE.message, SWARM_NETWORK_FAILURE.retryable);
    }
    if (!res.ok) {
      alive();
      const answer = (await res.json().catch(() => null)) as { code?: unknown; error?: unknown } | null;
      const code = typeof answer?.code === 'string' ? answer.code : undefined;
      // A refused fallback never costs the broadcast: go live once more without it.
      if (res.status === 400 && code === 'INVALID_FALLBACK' && body.fallback !== undefined) {
        dropCompatPlan(r, `the server refused the fallback${typeof answer?.error === 'string' ? `: ${answer.error}` : ''}`);
        return goLive(r);
      }
      const failure = describeSwarmFailure(res.status, code);
      throw new SwarmError(failure.message, failure.retryable);
    }
    // Recorded before any stop check, so every later failure path still ends the session it opened,
    // and ends it by its own keyId (a stop may have arrived while go-live was in flight).
    r.wentLive = true;
    // Delivered (unless an edit replaced it meanwhile, which is still owed).
    if (r.options.title === title) r.options = { ...r.options, title: undefined };
    let live: GoLive | null;
    try {
      live = parseGoLive(await res.json());
    } catch {
      live = null;
    }
    if (live) {
      r.liveKeyId = live.keyId;
      // A granted fallback proves support even from a backend that does not announce the capability, and replaces
      // an older remembered `false`.
      if (r.options.h264Fallback === 'auto') fallbackCapability.set(r.base, live.fallbackRenditions || live.fallback !== null);
    }
    alive();
    if (!live) throw new SwarmError('ompeer.com answered go-live without the expected session details.', false);
    if (r.transportProfile && (!live.transportProfile ||
        Object.entries(r.transportProfile).some(([key, value]) => live!.transportProfile![key as keyof MediaTransportProfile] !== value))) {
      throw new SwarmError('The server did not accept the delivery parts profile. Update the server before enabling 200 ms parts.', false);
    }
    if (!isSafeSignalingUrl(live.signalingUrl)) {
      throw new SwarmError(
        `ompeer.com handed out the signalling URL "${live.signalingUrl}"; refusing to send the seed token ` +
          'over it. Only wss:// (or ws:// to localhost) is accepted.',
        false,
      );
    }
    // The key lives only in memory for the duration of this session.
    const key = await importAesKey(base64ToBytes(live.aesKey), ['encrypt']);
    alive();
    return { live, key };
  }

  function onSignalingRejected(r: Run, engine: SwarmEngine, info: SignalingRejection): void {
    if (run !== r || (r.engine !== engine && r.pendingEngine !== engine)) return;
    logger.log('warn', SCOPE, `the tracker closed the seed connection: ${info.code} ${info.reason}`);
    const terminal = terminalRejection(info.code);
    if (terminal !== null) fail(r, terminal);
    else if (info.willRetry !== true) relive(r, `the tracker refused the seed connection (${info.code})`);
  }

  /** Seed engine for one go-live session, joined. Destroys the engine itself when joining fails. */
  async function connect(r: Run, live: GoLive): Promise<SwarmEngine> {
    // Project settings may contribute STUN addresses, never a permanent relay credential for cloud swarms.
    const configuredStun = splitIceServers(r.options.iceServers).stunUrls;
    const stunUrls = configuredStun.length ? configuredStun : ['stun:stun.l.google.com:19302'];
    let turnServers: RTCIceServer[] = [];
    let engine: SwarmEngine | null = null;
    let lastIceStatus = '';
    const lease = startIceLease({
      url: apiPath(r, 'ice'), method: 'POST', credentials: 'omit',
      headers: authHeaders(r, false), fetch: doFetch,
      onIceServers: servers => {
        turnServers = servers;
        engine?.configureIceServers?.([{ urls: stunUrls }, ...servers], 'all');
        // The compat seed shares this lease.
        if (r.compat || r.compatPlan) {
          r.ice = { stunUrls, turnServers: servers };
          if (r.compat?.engine && (r.engine === engine || r.pendingEngine === engine)) r.compat.engine.configureIceServers?.([{ urls: stunUrls }, ...servers], 'all');
        }
      },
      onStatus: status => {
        if (status === lastIceStatus) return;
        lastIceStatus = status;
        logger.log(status === 'ready' ? 'info' : 'warn', SCOPE,
          status === 'ready' ? 'Session TURN lease ready' : 'TURN broker unavailable; STUN-only');
      },
      onSessionExpired: () => { if (run === r) fail(r, 'The ICE broker rejected this stream key. Reconnect the channel to continue.'); },
    });
    const stopLease = () => lease.stop();
    r.cancel.signal.addEventListener('abort', stopLease, { once: true });
    await lease.ready;
    if (run !== r || r.cancel.signal.aborted) {
      lease.stop(); r.cancel.signal.removeEventListener('abort', stopLease); throw new Superseded();
    }
    if (r.compatPlan) r.ice = { stunUrls, turnServers };
    try { engine = makeEngine({
      signalingUrl: live.signalingUrl,
      role: 'seed',
      peerId: live.seedPeerId,
      seedToken: live.seedToken,
      signalingTokenTs: live.ts,
      // Without it a 4401 (e.g. tracker restart) falls into the engine's endless legacy backoff and
      // recovery waits for the heartbeat. With it the engine retries once with this session's token,
      // then emits signaling-rejected with willRetry false, which triggers the re-go-live at once.
      getSignalingToken: async () => ({ token: live.seedToken, ts: live.ts }),
      signalingTokenStreamId: live.streamId,
      keyId: live.keyId,
      segmentDurationMs: r.options.segmentMs,
      transportProfile: r.transportProfile,
      transportCapabilities: r.transportProfile ? { transportVersions: [1, 2], videoCodecs: [r.preflight?.codec ?? 'h264'] } : undefined,
      // Hidden: viewers never reach this seed (the tracker drops their offers too); it answers nobody.
      ...(live.hidesSeed ? { acceptPeerConnections: false, seedFirstHopFanout: 0 } : { seedFirstHopFanout: SEED_FIRST_HOP_FANOUT }),
      ...(stunUrls.length > 0 ? { stunUrls } : {}),
      ...(turnServers.length > 0 ? { turnServers } : {}),
    }); } catch (error) {
      lease.stop(); r.cancel.signal.removeEventListener('abort', stopLease); throw error;
    }
    iceLeases.set(engine, { stop() { lease.stop(); r.cancel.signal.removeEventListener('abort', stopLease); } });
    let joined = false;
    let rejectJoin: (err: Error) => void = () => {};
    const refused = new Promise<never>((_, reject) => {
      rejectJoin = reject;
    });
    refused.catch(() => undefined);
    engine.on?.('signaling-rejected', (info) => {
      const terminal = terminalRejection(info.code);
      if (info.willRetry === true && terminal === null) return;
      if (joined) {
        onSignalingRejected(r, engine, info);
        return;
      }
      // Rejected before the join completed: fail this attempt now instead of waiting for the timeout.
      rejectJoin(
        terminal !== null
          ? new SwarmError(terminal, false)
          : new SwarmError(`The ompeer.com tracker refused the seed connection (${info.code} ${info.reason}).`, true),
      );
    });
    // A socket that dropped and joined again: if this studio is exposed, say so again, so a tracker that lost it (a restart)
    // does not hide the seed from viewers who would then get nothing from a studio that answers them.
    engine.on?.('signaling-reconnected', () => {
      if (r.engine === engine && r.exposed) engine.announceExposure?.(true, 'reconnected');
    });
    try {
      // The engine reconnects its signalling socket on its own; a join that never completes means
      // the tracker is unreachable.
      // A stop does not wait for the join timeout: the engine is destroyed right away.
      const cancelled = new Promise<never>((_, reject) => {
        const onCancel = (): void => reject(new Superseded());
        if (r.cancel.signal.aborted) onCancel();
        else r.cancel.signal.addEventListener('abort', onCancel, { once: true });
      });
      cancelled.catch(() => undefined);
      await Promise.race([
        withTimeout(engine.join(live.streamId, 'main'), JOIN_TIMEOUT_MS, 'Could not connect to the ompeer.com tracker in time.'),
        refused,
        cancelled,
      ]);
    } catch (err) {
      destroyEngine(engine);
      throw err;
    }
    joined = true;
    return engine;
  }

  /**
   * Installs the fallback lane for a go-live answer. A server that did not take it (no `fallback`: flag off or an older
   * backend) or echoed another transport profile leaves the broadcast AV1-only; nothing else changes.
   */
  function startCompat(r: Run, live: GoLive, previous: CompatLane | null): void {
    if (previous && compatGone(previous)) return;
    const why = !live.fallback ? 'the server did not take the H.264 fallback'
      : !sameProfile(r.compatPlan!.transportProfile, live.fallback.transportProfile) ? 'the server changed the fallback delivery profile' : null;
    if (why) {
      if (previous) retireCompat(r, previous, 'unavailable', why, false);
      else if (!live.fallbackRenditions) {
        // A server without the feature: nothing to show (the next broadcast skips the probe).
        r.compatPlan = null;
        logger.log('info', SCOPE, `publishing AV1 only: ${why}`);
      } else { r.compatNote = why; logger.log('info', SCOPE, `publishing AV1 only: ${why}`); }
      return;
    }
    r.compat = compatSession(r, live, live.fallback!, previous);
    r.compatNote = null;
    if (r.compat.init?.sha256) syncCompat(r, r.compat);
  }

  /** go-live → key import → seed engine → join → segmenter. Throws SwarmError or Superseded. */
  async function establish(r: Run): Promise<void> {
    const alive = (): void => {
      if (run !== r) throw new Superseded();
    };
    if (r.transportProfile) {
      // Delivery parts carry H.264 or AV1. A VP8/VP9 preference (shared with the p2p modes) means Auto here,
      // as it already meant H.264 on 1 s/2 s segments, so moving a project to 200 ms never stops it going live.
      const preference = r.options.codec === 'h264' || r.options.codec === 'av1' ? r.options.codec : 'auto';
      const audio = r.options.stream.getAudioTracks?.()[0]?.getSettings?.();
      // The H.264 fallback is proven only when it can matter: the preflight runs it after an AV1 pick only.
      let fallback = r.options.h264Fallback === 'auto' && fallbackCapability.get(r.base) !== false
        ? compatVideo({ width: r.options.width, height: r.options.height, fps: r.options.fps, videoKbps: r.options.videoKbps }) : undefined;
      // Settings the backend would refuse (e.g. audio under 32 kb/s) are known now: no probe for a fallback never offered.
      // The codecs are a stand-in here (only the H.264 family is checked); the preflight supplies the real ones.
      const refused = fallback && fallbackRequest(r, { codecs: 'avc1.64001f,mp4a.40.2', transportProfile: r.transportProfile }, fallback);
      if (fallback && typeof refused === 'string') {
        r.compatNote = `the fallback was not offered: ${refused}`;
        logger.log('warn', SCOPE, `publishing without the H.264 fallback: ${refused}`);
        fallback = undefined;
      }
      r.preflight = await preflight({ ...r.options, codec:preference, sampleRate:audio?.sampleRate,
        numberOfChannels:audio?.channelCount, signal:r.cancel.signal, ...(fallback ? { fallback } : {}) });
      alive();
      if (r.preflight.fallbackReason) logger.log('info', SCOPE, `publishing ${r.preflight.codec.toUpperCase()}; not ${r.preflight.fallbackReason}`);
      if (fallback && r.preflight.codec === 'av1' && r.preflight.fallback) {
        r.compatPlan = {
          video: fallback, videoConfig: r.preflight.fallback.videoConfig, codecs: r.preflight.fallback.codecs,
          transportProfile: { transportVersion: 2, partDurationMs: 200, gopDurationMs: 1000,
            mediaBytesPerSecond: Math.ceil((fallback.videoKbps + r.options.audioKbps) * 1000 / 8 * 1.15) },
        };
        logger.log('info', SCOPE, `H.264 fallback ready: ${fallback.width}x${fallback.height}@${fallback.fps} ${fallback.videoKbps} kbps (${r.preflight.fallback.videoConfig.hardwareAcceleration ?? 'no-preference'})`);
      } else if (fallback && r.preflight.codec === 'av1') {
        r.compatNote = `no H.264 encoder for the fallback: ${r.preflight.fallbackError ?? 'preflight failed'}`;
        logger.log('warn', SCOPE, `publishing AV1 only: ${r.compatNote}. Viewers without AV1 get no video.`);
      }
    } else if (r.options.codec === 'av1') {
      throw new SwarmError('AV1 publication requires the opt-in 200 ms delivery profile.',false);
    }
    const { live, key } = await goLive(r);
    r.key = key;
    r.keyId = live.keyId;
    r.broadcastId = live.streamId === r.streamId ? live.broadcastId : null;
    invalidatePublication(r);
    r.mediaProxy = live.mediaProxy;
    r.mediaIngest = live.mediaIngest;
    r.proxyAbort.abort();
    r.proxyAbort = new AbortController();
    r.proxyActive = live.proxyActive;
    r.proxyPollAfterMs = live.proxyPollAfterMs;
    r.hidesSeed = live.hidesSeed;
    r.proxyFailures = 0;
    r.exposed = false;
    r.exposurePermanent = false;
    r.proxyStreak = 0;
    store.set({ streamId: live.streamId, seedPeerId: live.seedPeerId, hidesSeed: live.hidesSeed, privacyWarning: null, exposed: false });
    r.engine = await connect(r, live);
    alive();
    if (r.compatPlan) startCompat(r, live, null);

    try {
      const compat = r.compat && !compatGone(r.compat) ? r.compat : null;
      r.segmenter = makeSegmenter(r.options.stream, makeSink(r), {
        segmentMs: r.options.segmentMs,
        width: r.options.width,
        height: r.options.height,
        fps: r.options.fps,
        videoKbps: r.options.videoKbps,
        audioKbps: r.options.audioKbps,
        codec: r.preflight?.codec ?? 'h264',
        videoConfig: r.preflight?.videoConfig,
        // Headless Chromium has no hardware encoders and rejects prefer-hardware.
        hardwareAcceleration: 'no-preference',
        ...(compat ? { lanes: [{ id: COMPAT_RENDITION_ID, codec: 'h264' as const, ...compat.video, videoConfig: r.compatPlan!.videoConfig }] } : {}),
      });
      await r.segmenter.start();
    } catch (err) {
      if (err instanceof Superseded) throw err;
      throw new SwarmError(`The browser could not start the ${r.preflight?.codec ?? 'H.264'} segmenter: ${errorMessage(err)}`, false);
    }
    alive();
  }

  /**
   * The control plane or the tracker no longer accepts this session: go live again with fresh
   * credentials and a new engine. The segmenter keeps running (seq continues); the stored init is
   * published to the new engine before any further segment. Segments emitted meanwhile are dropped.
   */
  function relive(r: Run, reason: string): void {
    if (run !== r || r.reliving || r.establishing) return;
    r.reliving = true;
    if (r.heartbeatTimer) clearTimeout(r.heartbeatTimer);
    if (r.thumbnailTimer) clearTimeout(r.thumbnailTimer);
    r.heartbeatTimer = r.thumbnailTimer = null;
    const stale = [r.engine, r.pendingEngine];
    r.engine = null;
    r.pendingEngine = null;
    r.key = null;
    r.mediaProxy = false;
    // The compat seed's session is over too; its lane waits for the new go-live (see startCompat).
    const compat = r.compat;
    if (compat && !compatGone(compat)) {
      stale.push(compat.engine);
      compat.engine = null;
      compat.publishedInit = null;
      compat.state = 'starting';
      compat.proxyAbort.abort();
      compat.mediaProxy = false;
    }
    // Not endStream(): the stream is not over, only this seed session is.
    for (const old of stale) {
      try {
        destroyEngine(old);
      } catch (err) {
        logger.log('debug', SCOPE, `destroying the old engine failed: ${errorMessage(err)}`);
      }
    }
    logger.log('warn', SCOPE, `${reason}; going live again`);
    r.reliveDone = (async () => {
      let lastError = reason;
      for (let attempt = 0; attempt < RELIVE_MAX_ATTEMPTS; attempt += 1) {
        store.set({ status: 'reconnecting', reconnectAttempts: attempt + 1 });
        await pause(r, reliveDelayMs(attempt));
        if (run !== r) return;
        let engine: SwarmEngine | null = null;
        try {
          const { live, key } = await goLive(r);
          engine = await connect(r, live);
          if (run !== r) {
            destroyEngine(engine);
            await postEnd(r);
            return;
          }
          const fresh = engine;
          r.pendingEngine = fresh;
          // Installed through the publish queue so nothing queued earlier reaches the new engine
          // ahead of its init segment.
          enqueue(r, {
            kind: 'other',
            label: 'new session',
            run: async () => {
              if (run !== r || r.pendingEngine !== fresh) return;
              r.pendingEngine = null;
              r.engine = fresh;
              r.publishedPart = null;
              r.proxyPart = null;
              r.proxyInitReady = false;
              r.key = key;
              r.keyId = live.keyId;
              r.broadcastId = live.streamId === r.streamId ? live.broadcastId : null;
              invalidatePublication(r);
              r.mediaProxy = live.mediaProxy;
              r.mediaIngest = live.mediaIngest;
              r.proxyAbort.abort();
              r.proxyAbort = new AbortController();
              r.proxyActive = live.proxyActive;
              r.proxyPollAfterMs = live.proxyPollAfterMs;
              r.hidesSeed = live.hidesSeed;
              r.proxyFailures = 0;
              r.exposed = false;
              r.exposurePermanent = false;
              r.proxyStreak = 0;
              store.set({ hidesSeed: live.hidesSeed, privacyWarning: null, exposed: false });
              // A new broadcast: an init resend owed to the old one says nothing about this one.
              r.proxyInitResent = false;
              if (r.init) {
                await fresh.publishInit(r.init.bytes, { codecs: r.init.codecs });
                // A new go-live is a new broadcast: its proxy needs the init before any segment.
                sendToProxy(r, 'init.mp4', r.init.bytes, live.keyId);
              }
              // Then compat registers its current init for the new generation and seeds again.
              if (r.compat && r.compatPlan) startCompat(r, live, r.compat);
            },
          });
          r.reliving = false;
          r.heartbeatFailures = 0;
          logger.log('info', SCOPE, `live again as ${live.seedPeerId} (key ${live.keyId})`);
          store.set({ status: 'live', error: null, reconnectAttempts: 0, streamId: live.streamId, seedPeerId: live.seedPeerId });
          scheduleHeartbeat(r, pollAfter(r));
          scheduleThumbnail(r, THUMBNAIL_FIRST_DELAY_MS);
          return;
        } catch (err) {
          if (err instanceof Superseded || run !== r) {
            if (r.wentLive) await postEnd(r);
            return;
          }
          if (err instanceof SwarmError && !err.retryable) {
            fail(r, err.message);
            return;
          }
          lastError = errorMessage(err);
          logger.log('warn', SCOPE, `going live again failed (attempt ${attempt + 1}): ${lastError}`);
        }
      }
      fail(r, `Lost the live session on ompeer.com and could not go live again after ${RELIVE_MAX_ATTEMPTS} attempts: ${lastError}`);
    })();
  }

  return {
    getState: store.get,
    subscribe: store.subscribe,
    subscribeProxyUploads: uploads.subscribe,
    getWitnessIdentity,
    cancelRuntimeEvidence(){runtimePublication?.cancel();runtimePublication=null;},
    beginRuntimeEvidence(options){
      const unavailable=()=>Promise.resolve({status:'unavailable' as const});
      if(runtimePublication?.current())return unavailable();
      const identity=Object.freeze({...options.identity}),now=performance.now();
      try {
        if(!sameWitnessIdentity(identity)||!options.current()||options.deadlineMonotonicMs<=now||options.deadlineMonotonicMs-now>5000)return unavailable();
        const attempt=run!.segmenter!.beginFrameEvidence!({id:options.challengeId,sourceId:identity.sourceId,expiresAtMonotonicMs:options.deadlineMonotonicMs});
        runtimePublication=new RuntimePublication({...options,identity},attempt,()=>sameWitnessIdentity(identity));
        return runtimePublication.result;
      }catch{return unavailable();}
    },
    beginWitnessUpload(options) {
      runtimePublication?.cancel();runtimePublication=null;
      const unavailable = () => Promise.resolve({ status: 'unavailable' as const });
      const session = { ...options.session }, identity = Object.freeze({ ...options.identity });
      const ending = JSON.stringify([session.sessionId, session.streamId, session.epoch, session.revision]);
      if (!options.enabled || session.state !== 'ENDING' ||
          witnessedEndings.has(ending) || witnessedEndings.size >= 64) return unavailable();
      // Consume the generation even when prerequisites fail; a caller must not silently retry it.
      witnessedEndings.add(ending);
      witness?.cancel();
      const now = performance.now();
      try {
        if (session.streamId !== identity.streamId || !sameWitnessIdentity(identity) || !options.current() || options.clock.sourceId !== identity.sourceId ||
            !Number.isFinite(now) || !Number.isFinite(options.deadlineMonotonicMs) ||
            options.deadlineMonotonicMs <= now || options.deadlineMonotonicMs - now > 5000) return unavailable();
        const mapping = options.clock.read();
        const r = run!;
        const video = parseInit(r.init!.bytes).find(track => track.handler === 'vide');
        if (!video) return unavailable();
        const attempt = r.segmenter!.beginFrameEvidence!({ id: mapping.challengeId, sourceId: identity.sourceId,
          expiresAtMonotonicMs: options.deadlineMonotonicMs });
        witness = new WitnessPublication({ ...options, identity: { ...identity }, session: { ...session } }, attempt, video.trackId,
          () => sameWitnessIdentity(identity), Object.freeze({ ...mapping }));
        return witness.result;
      } catch { return unavailable(); }
    },

    async start(options) {
      if (run) throw new Error('Swarm publishing is already running; stop it first.');
      // A previous run may still be ending its session; its /end must land before this go-live.
      await retiring;
      if (run) throw new Error('Swarm publishing is already running; stop it first.');
      const refuse = (message: string): never => {
        store.set({ ...IDLE, status: 'error', error: message });
        throw new Error(message);
      };
      const parsed = parseStreamKey(options.streamKey);
      if (!parsed) refuse('The stream key is not valid. It looks like sk_<stream>.<channel>.<secret>; copy it again from ompeer.com.');
      if (!isWhipEndpoint(options.apiUrl)) refuse(`"${options.apiUrl}" is not an http(s) API URL.`);
      if (!isTokenSafeEndpoint(options.apiUrl)) {
        refuse(
          'Refusing to send the stream key over unencrypted http://. Use an https:// API URL ' +
            '(plain http is only accepted for localhost).',
        );
      }
      const now = Date.now();
      const r: Run = {
        publicationGeneration: ++publicationGeneration, broadcastId: null,
        options,
        preflight: null,
        compatPlan: null,
        compat: null,
        compatNote: null,
        ice: null,
        proxyBudgetDrops: 0,
        publishBudgetDrops: 0,
        load: { primed: false, droppedFrames: 0, publishDrops: 0, proxyDrops: 0, dropTicks: 0, queueTicks: 0, quietUntil: 0 },
        transportProfile: options.segmentMs === 200 ? {
          transportVersion: 2, partDurationMs: 200, gopDurationMs: 1000,
          mediaBytesPerSecond: Math.ceil((options.videoKbps + options.audioKbps) * 1000 / 8 * 1.15),
        } : undefined,
        publishedPart: null,
        proxyPart: null,
        proxyInFlight: new Set(),
        proxySent: null,
        mediaIngest: null,
        proxyPendingBytes: 0,
        proxyInitRevision: 0,
        proxyInitReady: false,
        base: options.apiUrl.trim().replace(/\/+$/, ''),
        streamId: parsed!.streamId,
        abort: new AbortController(),
        cancel: new AbortController(),
        liveKeyId: null,
        reliveDone: null,
        establishing: true,
        inFlight: null,
        wentLive: false,
        engine: null,
        segmenter: null,
        key: null,
        keyId: '',
        queue: [],
        pumping: null,
        init: null,
        mediaProxy: false,
        proxyActive: false,
        proxyPollAfterMs: HEARTBEAT_INTERVAL_MS,
        proxyAbort: new AbortController(),
        proxyChain: Promise.resolve(),
        proxyPending: 0,
        proxyInitResent: false,
        hidesSeed: false,
        proxyFailures: 0,
        exposed: false,
        exposurePermanent: false,
        proxyStreak: 0,
        droppedSegments: 0,
        reliving: false,
        pendingEngine: null,
        reliveTimer: null,
        heartbeatTimer: null,
        thumbnailTimer: null,
        thumbnailBusy: false,
        statsTimer: null,
        heartbeatFailures: 0,
        liveSeq: null,
        lastCapturedAt: null,
        codecs: null,
        lastBytes: 0,
        lastBytesAt: now,
        uploadKbps: 0,
        encodedSamples: [],
        lastEncodedAt: null,
        closed: false,
      };
      run = r;
      store.set({ ...IDLE, status: 'connecting' });
      const attempt = establish(r).catch(async (err: unknown) => {
        // Whatever the attempt created (engine, segmenter, the live session) is released before
        // stop() or the caller hears about it.
        await teardown(r, false);
        throw err;
      });
      r.inFlight = attempt.then(
        () => {},
        () => {},
      );
      try {
        await attempt;
      } catch (err) {
        r.establishing = false;
        if (err instanceof Superseded || run !== r) {
          // An encoder failure during start-up has already been reported through fail().
          throw new Error(store.get().error ?? 'Swarm publishing was stopped.');
        }
        // The first attempt is not retried: a wrong key or URL should reach the operator at once.
        const message = errorMessage(err);
        run = null;
        logger.log('error', SCOPE, message);
        store.set({ ...IDLE, status: 'error', error: message });
        throw new Error(message);
      }
      r.establishing = false;
      if (run !== r) throw new Error(store.get().error ?? 'Swarm publishing was stopped.');
      store.set({ status: 'live', error: null, reconnectAttempts: 0 });
      scheduleHeartbeat(r, pollAfter(r));
      scheduleThumbnail(r, THUMBNAIL_FIRST_DELAY_MS);
      r.statsTimer = setInterval(() => sampleStats(r), STATS_INTERVAL_MS);
      sampleStats(r);
    },

    async stop() {
      const r = run;
      run = null;
      store.set(IDLE);
      if (!r) {
        await retiring;
        return;
      }
      invalidatePublication(r);
      r.cancel.abort();
      await retire(async () => {
        if (r.establishing) {
          // The attempt notices the stop after its current step (a join is cut short by the cancel)
          // and tears down what it created, including ending a session go-live already opened.
          // Only a server that never answers the go-live is cut off, after the grace.
          let timer: ReturnType<typeof setTimeout> | undefined;
          await Promise.race([
            r.inFlight,
            new Promise<void>((resolve) => {
              timer = setTimeout(resolve, STOP_START_GRACE_MS);
            }),
          ]);
          clearTimeout(timer);
          r.abort.abort();
          await r.inFlight;
          // Covers an attempt that had just finished when the stop arrived; teardown is idempotent.
          await teardown(r, false);
        } else {
          await teardown(r, true);
        }
        await r.reliveDone;
      });
    },

    async getStats() {
      const r = run;
      if (!r || r.establishing) return null;
      return readStats(r);
    },

    async replaceStream(stream) {
      const r = run;
      if (!r) return;
      invalidatePublication(r);
      r.options = { ...r.options, stream };
      r.segmenter?.replaceTracks(stream);
    },

    async updateEncoding(options) {
      const r = run;
      if (!r) return;
      if (options.codec !== undefined || options.degradation !== undefined) {
        if (r.transportProfile && options.codec !== undefined && options.codec !== r.options.codec) throw new Error('Changing the publication codec requires a new broadcast.');
        logger.log('debug', SCOPE, 'publication codec is fixed for this broadcast; degradation preference does not change it');
      }
      const patch: { videoKbps?: number; audioKbps?: number } = {};
      if (options.videoKbps !== undefined) patch.videoKbps = options.videoKbps;
      if (options.audioKbps !== undefined) patch.audioKbps = options.audioKbps;
      if (r.transportProfile && ((patch.videoKbps ?? r.options.videoKbps) + (patch.audioKbps ?? r.options.audioKbps))*1000/8*1.15 > r.transportProfile.mediaBytesPerSecond) {
        throw new Error('Increasing bitrate beyond this broadcast capacity budget requires a new broadcast.');
      }
      // The fallback lane carries the same AAC: an audio bitrate beyond its own capacity budget ends it, not the change.
      const c = r.compat;
      if (c && !compatGone(c) && patch.audioKbps !== undefined &&
          (c.video.videoKbps + patch.audioKbps) * 1000 / 8 * 1.15 > c.transportProfile.mediaBytesPerSecond) {
        shedCompat(r, c, 'the audio bitrate went beyond the fallback capacity budget');
      }
      r.options = { ...r.options, ...patch };
      if (Object.keys(patch).length > 0) { invalidatePublication(r); r.segmenter?.updateEncoding(patch); }
    },

    setClips(enabled) {
      const r = run;
      if (!r || (r.options.clips !== false) === enabled) return;
      r.options = { ...r.options, clips: enabled };
      if (!enabled) clipsOf(r).window.clear();
    },

    async updateInfo(patch) {
      const r = run;
      if (!r || !r.wentLive) throw new Error('Not streaming to ompeer.com: the stream info is sent when you go live.');
      const body: SwarmInfoPatch = {};
      if (patch.title !== undefined && patch.title !== '') body.title = patch.title;
      if (patch.category !== undefined) body.category = patch.category || null;
      if (patch.tags !== undefined) body.tags = patch.tags;
      if (patch.language !== undefined) body.language = patch.language || null;
      if (patch.contentLabels !== undefined) body.contentLabels = patch.contentLabels;
      if (Object.keys(body).length === 0) return;
      // Remembered before the request: a re-go-live after a drop must carry the edit even when this
      // PATCH is the thing that failed. (The title only until the control plane has it.)
      r.options = {
        ...r.options,
        ...(body.title !== undefined ? { title: body.title } : {}),
        ...(body.category !== undefined ? { category: body.category ?? '' } : {}),
        ...(body.tags !== undefined ? { tags: body.tags } : {}),
        ...(body.language !== undefined ? { language: body.language ?? '' } : {}),
        ...(body.contentLabels !== undefined ? { contentLabels: body.contentLabels } : {}),
      };
      let res: Response;
      try {
        res = await doFetch(`${r.base}/api/streams/${encodeURIComponent(r.streamId)}`, {
          method: 'PATCH',
          headers: authHeaders(r, true),
          body: JSON.stringify(body),
          credentials: 'omit',
          cache: 'no-store',
          signal: r.abort.signal,
        });
      } catch {
        throw new Error(SWARM_NETWORK_FAILURE.message);
      }
      if (!res.ok) throw new Error(describeSwarmFailure(res.status, await readErrorCode(res)).message);
      if (body.title !== undefined && run === r && r.options.title === body.title) r.options = { ...r.options, title: undefined };
      logger.log('info', SCOPE, 'stream info updated on the control plane');
    },
  };
}
